import { supabase } from './supabase'

const API_BASE = '/api'

// ═══════════════════════════════════════════════════════════════════════════
// Auth Helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the current access token from Supabase session.
 * Returns null if not authenticated.
 */
async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Get auth headers for authenticated requests.
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export interface CardSummary {
  card_id: string
  type: string
  domain: string | null
  phase: string
  title: string | null
  summary: string
  tags: string[]
  quality_score: number
  support_count: number
  edge_count: number
  status: string
  created_at: string
}

export interface CardDetail extends CardSummary {
  sources: Source[]
  outgoing_edges: Edge[]
  incoming_edges: Edge[]
}

export interface Source {
  source_id: string
  title: string | null
  filename: string | null
  domain_hint: string | null
  tags: string[]
  project_tag: string | null
  source_date: string | null
  original_format: string | null
  extraction_method: string | null
  ingested_at: string
}

export interface SourceDetail extends Source {
  raw_text: string
  cards: CardSummary[]
}

export interface Edge {
  edge_id: string
  from_card_id: string
  to_card_id: string
  type: string
  weight: number
  context: string | null
  created_at: string
  linked_card_id: string
  linked_card_title: string | null
  linked_card_summary: string
}

export const EDGE_TYPES = [
  'supported_by',
  'explains',
  'causes',
  'prevents',
  'contrast',
  'example_of',
  'analogy_to',
  'applies_to',
] as const

export type EdgeType = (typeof EDGE_TYPES)[number]

export const EDGE_TYPE_LABELS: Record<EdgeType, string> = {
  supported_by: 'Supports',
  explains: 'Explains',
  causes: 'Causes',
  prevents: 'Prevents',
  contrast: 'Contrasts',
  example_of: 'Examples',
  analogy_to: 'Analogies',
  applies_to: 'Applies To',
}

export interface Domain {
  domain_id: string
  name: string
  internal_external: string | null
  macro_micro: string | null
  question: string | null
  description: string | null
}

export interface ReviewItem {
  review_id: string
  kind: string
  card_id: string | null
  other_card_id: string | null
  reason: string
  similarity_score: number | null
  status: string
  payload: Record<string, unknown> | null
  created_at: string
}

export interface Stats {
  total_cards: number
  total_sources: number
  total_edges: number
  cards_by_domain: Record<string, number>
  cards_by_phase: Record<string, number>
  cards_by_type: Record<string, number>
  review_pending: number
}

export interface PaginatedCards {
  cards: CardSummary[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface IngestionInput {
  raw_text: string
  title?: string
  filename?: string
  domain_hint?: string
  tags?: string[]
  project_tag?: string
  source_date?: string
  original_format?: string
  extraction_method?: string
  session_id?: string
}

export interface IngestionResponse {
  message: string
  session_id: string
  event_id: string
  task_id: string
  workflow: string
  filename: string | null
}

export interface TaskStatus {
  task_id: string
  status: string
  result?: unknown
  error?: string
  info?: Record<string, unknown>
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || `HTTP ${response.status}`)
  }

  return response.json()
}

// Card endpoints
export async function fetchCards(params: {
  domain?: string
  phase?: string
  type?: string
  tags?: string
  status?: string
  search?: string
  page?: number
  page_size?: number
  sort_by?: string
  sort_order?: string
}): Promise<PaginatedCards> {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, String(value))
    }
  })
  return fetchApi<PaginatedCards>(`/cards/?${searchParams.toString()}`)
}

export async function fetchCard(cardId: string): Promise<CardDetail> {
  return fetchApi<CardDetail>(`/cards/${cardId}`)
}

export async function fetchDomains(): Promise<Domain[]> {
  return fetchApi<Domain[]>('/cards/domains')
}

export async function fetchTags(search?: string): Promise<string[]> {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  return fetchApi<string[]>(`/cards/tags${params}`)
}

export interface SemanticSearchResult {
  cards: CardSummary[]
  query_embedding_generated: boolean
}

export async function semanticSearch(params: {
  query: string
  limit?: number
  domain?: string
  phase?: string
}): Promise<SemanticSearchResult> {
  const searchParams = new URLSearchParams()
  searchParams.set('query', params.query)
  if (params.limit) searchParams.set('limit', String(params.limit))
  if (params.domain) searchParams.set('domain', params.domain)
  if (params.phase) searchParams.set('phase', params.phase)

  return fetchApi<SemanticSearchResult>(`/cards/search/semantic?${searchParams.toString()}`, {
    method: 'POST',
  })
}

export async function fetchSource(sourceId: string): Promise<SourceDetail> {
  return fetchApi<SourceDetail>(`/cards/sources/${sourceId}`)
}

export async function updateSource(
  sourceId: string,
  update: { title?: string }
): Promise<SourceDetail> {
  return fetchApi<SourceDetail>(`/cards/sources/${sourceId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  })
}

export async function fetchSources(params: {
  project_tag?: string
  page?: number
  page_size?: number
}): Promise<Source[]> {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, String(value))
    }
  })
  return fetchApi<Source[]>(`/cards/sources/?${searchParams.toString()}`)
}

export async function fetchReviewQueue(params: {
  status?: string
  kind?: string
  page?: number
  page_size?: number
}): Promise<ReviewItem[]> {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, String(value))
    }
  })
  return fetchApi<ReviewItem[]>(`/cards/review-queue/?${searchParams.toString()}`)
}

export async function updateReviewItem(
  reviewId: string,
  update: { status: string; notes?: string }
): Promise<{ message: string; review_id: string; status: string }> {
  return fetchApi(`/cards/review-queue/${reviewId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  })
}

export async function fetchStats(): Promise<Stats> {
  return fetchApi<Stats>('/cards/stats/')
}

// Ingestion endpoints
export async function submitIngestion(
  input: IngestionInput
): Promise<IngestionResponse> {
  return fetchApi<IngestionResponse>('/events/card-ingestion/', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function fetchTaskStatus(taskId: string): Promise<TaskStatus> {
  return fetchApi<TaskStatus>(`/tasks/${taskId}`)
}

// Poll task until completion
export async function pollTaskCompletion(
  taskId: string,
  interval = 2000,
  timeout = 600000
): Promise<TaskStatus> {
  const startTime = Date.now()

  while (Date.now() - startTime < timeout) {
    const status = await fetchTaskStatus(taskId)

    if (status.status === 'COMPLETED' || status.status === 'FAILED') {
      return status
    }

    await new Promise((resolve) => setTimeout(resolve, interval))
  }

  throw new Error('Task polling timeout')
}

// WebSocket connection for real-time workflow progress
export interface WorkflowMessage {
  type: 'node_start' | 'node_complete' | 'workflow_complete' | 'status_update' | 'workflow_error'
  node?: string
  next?: string
  output?: Record<string, unknown>
  status?: string
  error?: string
}

export function subscribeToWorkflow(
  taskId: string,
  onMessage: (message: WorkflowMessage) => void,
  onError?: (error: Event) => void,
  onClose?: () => void
): WebSocket {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsUrl = `${wsProtocol}//${window.location.host}/ws/workflow/${taskId}`

  const ws = new WebSocket(wsUrl)

  ws.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data) as WorkflowMessage
      onMessage(message)
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e)
    }
  }

  ws.onerror = (event) => {
    console.error('WebSocket error:', event)
    onError?.(event)
  }

  ws.onclose = () => {
    onClose?.()
  }

  return ws
}

// Get the final output from a completed task result
export function getSessionManagerOutput(result: unknown): SessionManagerOutput | null {
  if (!result || typeof result !== 'object') return null

  const taskContext = result as { nodes?: Record<string, unknown> }
  const sessionOutput = taskContext.nodes?.SessionManagerNode as SessionManagerOutput | undefined

  return sessionOutput ?? null
}

export interface SessionManagerOutput {
  source_id: string
  is_duplicate_source: boolean
  cards_extracted: number
  cards_new: number
  cards_merged: number
  cards_related: number
  edges_created: number
  edges_reinforced: number
  review_items: number
  cards: CardOutput[]
  edges: EdgeOutput[]
  reviews: ReviewOutput[]
  report: string | null
}

export interface CardOutput {
  card_id: string
  type: string
  domain: string
  phase: string
  title: string | null
  summary: string
  tags: string[]
  quality_score: number
  is_new: boolean
  canonical_id: string | null
  edges_count: number
}

export interface EdgeOutput {
  edge_id: string
  from_card_id: string
  to_card_id: string
  type: string
  weight: number
  is_new: boolean
}

export interface ReviewOutput {
  review_id: string
  kind: string
  reason: string
  payload: Record<string, unknown>
}

// Interview / Card Composition types
export interface RagQuery {
  query: string
  reasoning: string
  priority: number
  target_card_types?: string[]
}

export interface InterviewSubmitResponse {
  message: string
  session_id: string
  event_id: string
  task_id: string
  workflow: string
  force_summary: boolean
}

export interface InterviewResponse {
  coaching_message: string
  question: string | null
  current_queries: RagQuery[]
  narrative_direction: string
  key_themes: string[]
  summary: string | null
  ready_for_retrieval: boolean
  session_id: string
  turn_count: number
}

// Interview / Card Composition endpoints
export async function submitInterview(params: {
  content: string
  session_id?: string
  force_summary?: boolean
}): Promise<InterviewSubmitResponse> {
  return fetchApi<InterviewSubmitResponse>('/events/card-composition/', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// SSE streaming chunk format
export interface StreamingChunk {
  object: 'chat.completion.chunk'
  model: string
  choices: Array<{
    index: number
    delta: {
      role: string
      content: Partial<InterviewResponse>
    }
    finish_reason: string | null
  }>
}

export interface StreamingDoneEvent {
  type: 'done'
  output: InterviewResponse
}

export interface StreamingTextCompleteEvent {
  type: 'text_complete'
}

export interface StreamingThinkingStartEvent {
  type: 'thinking_start'
}

export interface StreamingThinkingEvent {
  type: 'thinking'
  thinking: string
}

export interface StreamingThinkingCompleteEvent {
  type: 'thinking_complete'
  thinking: string
}

export type StreamEvent =
  | StreamingChunk
  | StreamingDoneEvent
  | StreamingTextCompleteEvent
  | StreamingThinkingStartEvent
  | StreamingThinkingEvent
  | StreamingThinkingCompleteEvent

export type BuildMode = 'brainstorm' | 'foolscap' | 'draft'

// SSE streaming interview submission
export async function streamInterview(
  params: {
    content: string
    session_id?: string
    force_summary?: boolean
    mode?: BuildMode
  },
  callbacks: {
    onChunk: (chunk: Partial<InterviewResponse>) => void
    onTextComplete?: () => void  // Called when text streaming is done, before JSON parsing
    onThinkingStart?: () => void  // Called when extended thinking starts
    onThinkingDelta?: (delta: string) => void  // Called for each thinking token
    onThinkingComplete?: () => void  // Called when thinking is done
    onDone: (output: InterviewResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  // Map old params to new build schema
  const buildParams = {
    content: params.content,
    session_id: params.session_id,
    mode: params.mode || 'brainstorm',
  }

  const response = await fetch(`${API_BASE}/events/build/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(buildParams),
  })

  if (!response.ok) {
    const error = await response.text()
    callbacks.onError(error || `HTTP ${response.status}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Process SSE format: "data: {...}\n\n"
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || '' // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6) // Remove "data: " prefix

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data) as StreamEvent

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                callbacks.onDone(event.output)
                break
              case 'text_complete':
                // Text streaming finished, JSON still processing - unlock input
                callbacks.onTextComplete?.()
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
            }
          } else if ('object' in event && event.object === 'chat.completion.chunk') {
            const content = event.choices?.[0]?.delta?.content
            if (content) {
              callbacks.onChunk(content)
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export function getIntentParseOutput(result: unknown): InterviewResponse | null {
  if (!result || typeof result !== 'object') return null

  const taskContext = result as { nodes?: Record<string, unknown> }
  const intentOutput = taskContext.nodes?.IntentParseNode as InterviewResponse | undefined

  return intentOutput ?? null
}

// Retrieval result types
export interface RetrievedCard {
  card_id: string
  type: string
  phase?: string           // Legacy (cp workflow)
  domain?: string          // Entity Framework space (f workflow)
  title: string | null
  summary: string
  tags: string[]
  quality_score: number
  support_count: number
  similarity: number
}

export interface QueryResult {
  query: string
  reasoning: string
  priority: number
  cards: RetrievedCard[]
  card_count: number
}

export interface RetrieveNodeOutput {
  query_results: QueryResult[]
  total_cards: number
  session_id: string
  skipped: boolean
}

export function getRetrieveNodeOutput(result: unknown): RetrieveNodeOutput | null {
  if (!result || typeof result !== 'object') return null

  const taskContext = result as { nodes?: Record<string, unknown> }
  const retrieveOutput = taskContext.nodes?.RetrieveNode as RetrieveNodeOutput | undefined

  return retrieveOutput ?? null
}

// Brainstorm types and functions
export interface BrainstormResponse {
  coaching_message: string
  question: string | null
  brainstorm_content: string
  cards_added: string[]
  brainstorm_modified: boolean
  session_context: {
    phase: string
    working_direction: string
    key_themes: string[]
    gaps: string[]
    rejected_cards: string[]
  } | null
  session_id: string
  skipped: boolean
}

export async function streamBrainstorm(
  params: {
    content: string
    session_id: string
    brainstorm_mode: boolean
    brainstorm_content?: string
    foolscap_content?: string
    mode: 'brainstorm' | 'foolscap' | 'draft'
  },
  callbacks: {
    onChunk: (chunk: Partial<BrainstormResponse>) => void
    onTextComplete?: () => void
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Coaching message callback (complete message, frontend animates typing)
    onCoachingMessage?: (content: string) => void
    // Document streaming callbacks (line-by-line for frontend diff computation)
    onDocumentStart?: () => void
    onDocumentLine?: (line: string) => void
    onDocumentComplete?: (fullDocument: string) => void
    // Scratchpad streaming callbacks (line-by-line for frontend diff computation)
    onScratchpadStart?: () => void
    onScratchpadLine?: (line: string) => void
    onScratchpadComplete?: (fullScratchpad: string) => void
    // Legacy: Instant document update - receives complete annotated document from backend
    onDocumentUpdate?: (content: string) => void
    onDone: (output: BrainstormResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  // Map old params to new build schema
  const buildParams = {
    content: params.content,
    session_id: params.session_id,
    mode: params.mode,
    brainstorm_content: params.brainstorm_content,
    foolscap_content: params.foolscap_content,
  }

  const response = await fetch(`${API_BASE}/events/build/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildParams),
  })

  if (!response.ok) {
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            // Debug logging for document streaming events
            if (event.type.startsWith('document')) {
              console.log('[streamBrainstorm] Document event:', event.type, event.content?.substring?.(0, 50) || '')
            }

            switch (event.type) {
              case 'done':
                // Only process done events from BrainstormNode (has coaching_message)
                if (event.output && !event.output.skipped && event.output.coaching_message) {
                  callbacks.onDone(event.output)
                }
                break
              case 'text_complete':
                callbacks.onTextComplete?.()
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              // Coaching message (complete, frontend animates typing effect)
              case 'coaching_message':
                callbacks.onCoachingMessage?.(event.content)
                break
              // Document streaming (line-by-line for frontend diff computation)
              case 'document_start':
                callbacks.onDocumentStart?.()
                break
              case 'document_line':
                callbacks.onDocumentLine?.(event.content)
                break
              case 'document_complete':
                callbacks.onDocumentComplete?.(event.content)
                break
              // Scratchpad streaming (line-by-line for frontend diff computation)
              case 'scratchpad_start':
                callbacks.onScratchpadStart?.()
                break
              case 'scratchpad_line':
                callbacks.onScratchpadLine?.(event.content)
                break
              case 'scratchpad_complete':
                callbacks.onScratchpadComplete?.(event.content)
                break
              // Legacy: Instant document update - receives complete annotated document from backend
              case 'document_update':
              case 'foolscap_update':
                callbacks.onDocumentUpdate?.(event.content)
                break
            }
          } else if ('object' in event && event.object === 'chat.completion.chunk') {
            const content = event.choices?.[0]?.delta?.content
            if (content) {
              // Content can be a string or an object with coaching_message
              if (typeof content === 'string') {
                callbacks.onChunk({ coaching_message: content })
              } else if (typeof content === 'object' && content.coaching_message) {
                callbacks.onChunk({ coaching_message: content.coaching_message })
              }
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export function getBrainstormNodeOutput(result: unknown): BrainstormResponse | null {
  if (!result || typeof result !== 'object') return null

  const taskContext = result as { nodes?: Record<string, unknown> }
  const brainstormOutput = taskContext.nodes?.BrainstormNode as BrainstormResponse | undefined

  return brainstormOutput ?? null
}

// Build workflow types and functions
// BuildMode already exported above at line 501

export interface BuildResponse {
  session_id: string
  mode: BuildMode
  session_saved: boolean
  message?: string
}

/**
 * Stream build workflow with mode selection.
 *
 * This is the new unified API for brainstorming, Foolscap structuring, and drafting.
 * Mode is set via UI buttons, not slash commands.
 */
export async function streamBuild(
  params: {
    content: string
    session_id?: string
    mode: BuildMode
    brainstorm_content?: string
    foolscap_content?: string
    draft_content?: string
  },
  callbacks: {
    onChunk: (chunk: Partial<BuildResponse>) => void
    onTextComplete?: () => void
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    onDone: (output: BuildResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const response = await fetch(`${API_BASE}/events/build/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                callbacks.onDone(event.output)
                break
              case 'text_complete':
                callbacks.onTextComplete?.()
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
            }
          } else if ('object' in event && event.object === 'chat.completion.chunk') {
            const content = event.choices?.[0]?.delta?.content
            if (content) {
              if (typeof content === 'string') {
                callbacks.onChunk({ message: content })
              } else if (typeof content === 'object') {
                callbacks.onChunk(content)
              }
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Build Session Management API
// ═══════════════════════════════════════════════════════════════════════════

export interface BuildSessionListItem {
  session_id: string
  name: string
  mode: BuildMode
  status: 'active' | 'archived'
  total_turns: number
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface BuildSessionTurn {
  turn_id: string
  session_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: BuildMode
  thinking: string | null
  timestamp: string
}

export interface BuildSession extends BuildSessionListItem {
  brainstorm_content: string | null
  new_brainstorm_content: string | null  // Clean content for frontend diff computation
  foolscap_content: string | null
  new_foolscap_content: string | null  // Clean content for frontend diff computation
  draft_content: string | null
  scratchpad_content: string | null
  new_scratchpad_content: string | null  // Clean content for frontend diff computation
  session_context: Record<string, unknown> | null
}

export interface BuildSessionWithTurns extends BuildSession {
  turns: BuildSessionTurn[]
}

export async function listBuildSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<BuildSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const response = await fetch(`${API_BASE}/build/sessions?${params}`)
  if (!response.ok) {
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

export async function createBuildSession(
  name?: string,
  mode: BuildMode = 'brainstorm'
): Promise<BuildSession> {
  const response = await fetch(`${API_BASE}/build/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, mode }),
  })
  if (!response.ok) {
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

export async function getBuildSession(sessionId: string): Promise<BuildSession> {
  const response = await fetch(`${API_BASE}/build/sessions/${sessionId}`)
  if (!response.ok) {
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function getBuildSessionWithTurns(sessionId: string): Promise<BuildSessionWithTurns> {
  const response = await fetch(`${API_BASE}/build/sessions/${sessionId}/full`)
  if (!response.ok) {
    throw new Error(`Failed to get session with turns: ${response.statusText}`)
  }
  return response.json()
}

export async function updateBuildSession(
  sessionId: string,
  updates: {
    name?: string
    mode?: BuildMode
    status?: 'active' | 'archived'
    brainstorm_content?: string
    foolscap_content?: string
    draft_content?: string
    scratchpad_content?: string
  }
): Promise<BuildSession> {
  const response = await fetch(`${API_BASE}/build/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteBuildSession(sessionId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/build/sessions/${sessionId}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

export async function getBuildSessionTurns(
  sessionId: string,
  limit?: number
): Promise<BuildSessionTurn[]> {
  const params = limit ? `?limit=${limit}` : ''
  const response = await fetch(`${API_BASE}/build/sessions/${sessionId}/turns${params}`)
  if (!response.ok) {
    throw new Error(`Failed to get session turns: ${response.statusText}`)
  }
  return response.json()
}

// ═══════════════════════════════════════════════════════════════════════════
// Zinsser Session Management API
// ═══════════════════════════════════════════════════════════════════════════

export type ZinsserMode = 'brainstorm' | 'foolscap' | 'draft' | 'gatekeeper' | 'responder'

export interface ZinsserSessionListItem {
  session_id: string
  name: string
  mode: ZinsserMode
  status: 'active' | 'archived'
  total_turns: number
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface ZinsserSessionTurn {
  turn_id: string
  session_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: ZinsserMode
  thinking: string | null
  timestamp: string
}

export interface ZinsserSession extends ZinsserSessionListItem {
  // Only 3 document fields needed:
  new_doc: string | null  // Current document (user's canvas)
  old_doc: string | null  // Previous version (baseline for diff)
  zin_doc_annotated: string | null  // Annotated version with <ins>/<del> tags for "Show AI Changes"
  session_context: Record<string, unknown> | null
}

export interface ZinsserSessionWithTurns extends ZinsserSession {
  turns: ZinsserSessionTurn[]
}

export async function listZinsserSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<ZinsserSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

export async function createZinsserSession(
  name?: string,
  mode: ZinsserMode = 'brainstorm'
): Promise<ZinsserSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ name, mode }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

export async function getZinsserSession(sessionId: string): Promise<ZinsserSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function getZinsserSessionWithTurns(sessionId: string): Promise<ZinsserSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session with turns: ${response.statusText}`)
  }
  return response.json()
}

export async function updateZinsserSession(
  sessionId: string,
  updates: {
    name?: string
    mode?: ZinsserMode
    status?: 'active' | 'archived'
    new_doc?: string  // Current canvas content
  }
): Promise<ZinsserSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteZinsserSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

export async function getZinsserSessionTurns(
  sessionId: string,
  limit?: number
): Promise<ZinsserSessionTurn[]> {
  const params = limit ? `?limit=${limit}` : ''
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/turns${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session turns: ${response.statusText}`)
  }
  return response.json()
}

// Zinsser streaming function (original)
export interface ZinsserBrainstormResponse {
  coaching_message: string
  brainstorm_content: string
  new_brainstorm_content: string
  last_agent_brainstorm_output: string
  brainstorm_modified: boolean
  scratchpad_content: string
  new_scratchpad_content: string
  scratchpad_modified: boolean
  session_context: {
    phase: string
    context_summary: string
    audience_understood: boolean
    purpose_understood: boolean
    key_insights: string[]
  } | null
  suggested_session_name: string | null
  session_id: string
  skipped: boolean
  thinking: string
}

export async function streamZinsser(
  params: {
    content: string
    session_id: string
    brainstorm_mode: boolean
    brainstorm_content?: string
    foolscap_content?: string
    mode: 'brainstorm' | 'foolscap' | 'draft'
  },
  callbacks: {
    // Thinking streaming callbacks
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Final output callback - receives complete JSON with annotated content
    onDone: (output: ZinsserBrainstormResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  // Map params to zinsser schema
  const zinsserParams = {
    content: params.content,
    session_id: params.session_id,
    mode: params.mode,
    brainstorm_content: params.brainstorm_content,
    foolscap_content: params.foolscap_content,
  }

  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(zinsserParams),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                // Process done events from ZinsserBrainstormNode (has coaching_message)
                if (event.output && !event.output.skipped && event.output.coaching_message) {
                  callbacks.onDone(event.output)
                }
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Z Session Management API (clone of Zinsser with separate tables)
// ═══════════════════════════════════════════════════════════════════════════

export type ZMode = 'brainstorm' | 'foolscap' | 'draft' | 'gatekeeper'

export interface ZSessionListItem {
  session_id: string
  name: string
  mode: ZMode
  status: 'active' | 'archived'
  total_turns: number
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface ZSessionTurn {
  turn_id: string
  session_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: ZMode
  thinking: string | null
  timestamp: string
}

export interface ZSession extends ZSessionListItem {
  new_doc: string | null
  old_doc: string | null
  foolscap_content: string | null
  new_foolscap_content: string | null
  draft_content: string | null
  scratchpad_content: string | null
  new_scratchpad_content: string | null
  session_context: Record<string, unknown> | null
}

export interface ZSessionWithTurns extends ZSession {
  turns: ZSessionTurn[]
}

export async function listZSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<ZSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

export async function createZSession(
  name?: string,
  mode: ZMode = 'brainstorm'
): Promise<ZSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ name, mode }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

export async function getZSession(sessionId: string): Promise<ZSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function getZSessionWithTurns(sessionId: string): Promise<ZSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session with turns: ${response.statusText}`)
  }
  return response.json()
}

export async function updateZSession(
  sessionId: string,
  updates: {
    name?: string
    mode?: ZMode
    status?: 'active' | 'archived'
    new_doc?: string
    old_doc?: string
    zin_doc?: string
    foolscap_content?: string
    new_foolscap_content?: string
    draft_content?: string
    scratchpad_content?: string
    new_scratchpad_content?: string
  }
): Promise<ZSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteZSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

export async function getZSessionTurns(
  sessionId: string,
  limit?: number
): Promise<ZSessionTurn[]> {
  const params = limit ? `?limit=${limit}` : ''
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/turns${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session turns: ${response.statusText}`)
  }
  return response.json()
}

// Z streaming function

// Diff statistics for final comparison
export interface DiffStats {
  blocks_added: number
  blocks_deleted: number
  blocks_modified: number
  blocks_unchanged: number
}

// Final diff comparing original document to fully edited document
export interface FinalDiff {
  has_changes: boolean
  original_doc: string
  final_doc: string
  stats: DiffStats
  changes: Array<{
    type: 'added' | 'deleted' | 'modified'
    content?: string
    modifications?: Array<{
      deleted: string
      added: string
      context: string
    }>
  }>
}

// Session complete output from SessionManagerNode
export interface SessionCompleteOutput {
  session_saved: boolean
  brainstorm_saved: boolean
  session_id: string
  mode: string
  session_name: string
  message: string
  new_doc: string | null
  annotated_doc: string | null  // Annotated markdown with <ins>/<del> tags
  final_diff: FinalDiff | null
}

export interface ZBrainstormResponse {
  coaching_message: string
  new_doc: string
  old_doc: string
  last_agent_brainstorm_output: string
  brainstorm_modified: boolean
  scratchpad_content: string
  new_scratchpad_content: string
  scratchpad_modified: boolean
  session_context: {
    phase: string
    context_summary: string
    audience_understood: boolean
    purpose_understood: boolean
    key_insights: string[]
  } | null
  suggested_session_name: string | null
  session_id: string
  skipped: boolean
  thinking: string
}

export async function streamZ(
  params: {
    content: string
    session_id: string
    brainstorm_mode: boolean
    new_doc?: string
    foolscap_content?: string
    mode: 'brainstorm' | 'foolscap' | 'draft'
  },
  callbacks: {
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    onDone: (output: ZBrainstormResponse) => void
    onSessionComplete?: (output: SessionCompleteOutput) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const zParams = {
    content: params.content,
    session_id: params.session_id,
    mode: params.mode,
    new_doc: params.new_doc,
    foolscap_content: params.foolscap_content,
  }

  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/z/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(zParams),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                if (event.output && !event.output.skipped && event.output.coaching_message) {
                  callbacks.onDone(event.output)
                }
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ============================================================================
// Z Gatekeeper Streaming
// ============================================================================

export interface ZGatekeeperResponse {
  response: string
  session_id: string
  thinking?: string
}

export interface ZinsserResponse {
  zin_doc: string
  diagnosis: {
    controlling_idea?: string
    audience_contract?: string
    structure_issues?: string[]
    evidence_gaps?: string[]
    opening_closing?: string
    voice_consistency?: string
  }
  changes_made: string[]
}

export interface ResponderResponse {
  response: string
  follow_up_question?: string | null
  thinking?: string
  session_id: string
}

export async function streamZGatekeeper(
  params: {
    content: string
    session_id: string
    new_doc?: string
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // Gatekeeper thinking (interview phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Gatekeeper done
    onGatekeeperDone?: (output: ZGatekeeperResponse) => void
    // Zinsser start (macro editing phase begins)
    onZinsserStart?: () => void
    // Zinsser thinking (macro editing phase)
    onZinsserThinkingStart?: () => void
    onZinsserThinkingDelta?: (delta: string) => void
    onZinsserThinkingComplete?: () => void
    // Zinsser done
    onZinsserDone?: (output: ZinsserResponse) => void
    // Zinsser text editor edits (streamed as they happen)
    onZinsserEdit?: (oldStr: string, newStr: string) => void
    onZinsserInsert?: (insertLine: number, insertText: string) => void
    // CutClutter (micro editing - Pass 1)
    onCutClutterStart?: () => void
    onCutClutterThinkingStart?: () => void
    onCutClutterThinkingDelta?: (delta: string) => void
    onCutClutterThinkingComplete?: () => void
    // CutClutter done
    onCutClutterDone?: (output: ZinsserResponse) => void
    // CutClutter text editor edits (streamed as they happen)
    onCutClutterEdit?: (oldStr: string, newStr: string) => void
    // StrengthenVerbs (micro editing - Pass 2)
    onStrengthenVerbsStart?: () => void
    onStrengthenVerbsThinkingStart?: () => void
    onStrengthenVerbsThinkingDelta?: (delta: string) => void
    onStrengthenVerbsThinkingComplete?: () => void
    // StrengthenVerbs done
    onStrengthenVerbsDone?: (output: ZinsserResponse) => void
    // StrengthenVerbs text editor edits (streamed as they happen)
    onStrengthenVerbsEdit?: (oldStr: string, newStr: string) => void
    // CheckLogic (micro editing - Pass 3)
    onCheckLogicStart?: () => void
    onCheckLogicThinkingStart?: () => void
    onCheckLogicThinkingDelta?: (delta: string) => void
    onCheckLogicThinkingComplete?: () => void
    // CheckLogic done
    onCheckLogicDone?: (output: ZinsserResponse) => void
    // CheckLogic text editor edits (streamed as they happen)
    onCheckLogicEdit?: (oldStr: string, newStr: string) => void
    // RhythmSound (micro editing - Pass 4)
    onRhythmSoundStart?: () => void
    onRhythmSoundThinkingStart?: () => void
    onRhythmSoundThinkingDelta?: (delta: string) => void
    onRhythmSoundThinkingComplete?: () => void
    // RhythmSound done
    onRhythmSoundDone?: (output: ZinsserResponse) => void
    // RhythmSound text editor edits (streamed as they happen)
    onRhythmSoundEdit?: (oldStr: string, newStr: string) => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Responder (coaching response after editing)
    onResponderStart?: () => void
    onResponderThinkingStart?: () => void
    onResponderThinkingDelta?: (delta: string) => void
    onResponderThinkingComplete?: () => void
    onResponderDone?: (output: ResponderResponse) => void
    // Legacy: called for gatekeeper done (backwards compat)
    onDone: (output: ZGatekeeperResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/z/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: params.content,
      session_id: params.session_id,
      new_doc: params.new_doc,
      mode: 'gatekeeper',
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                // Gatekeeper and Zinsser use generic 'done' event
                if (event.output?.zin_doc !== undefined && event.output?.diagnosis !== undefined) {
                  // This is Zinsser output (macro editing) - has zin_doc AND diagnosis
                  callbacks.onZinsserDone?.(event.output)
                } else if (event.output?.response) {
                  // This is Gatekeeper output
                  callbacks.onGatekeeperDone?.(event.output)
                  callbacks.onDone(event.output) // Legacy compat
                }
                break
              case 'cut_clutter_done':
                // CutClutter uses its own done event type
                callbacks.onCutClutterDone?.(event.output)
                break
              case 'strengthen_verbs_done':
                // StrengthenVerbs uses its own done event type
                callbacks.onStrengthenVerbsDone?.(event.output)
                break
              case 'check_logic_done':
                // CheckLogic uses its own done event type
                callbacks.onCheckLogicDone?.(event.output)
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              // Zinsser-specific events
              case 'zinsser_start':
                callbacks.onZinsserStart?.()
                break
              case 'zinsser_thinking_start':
                callbacks.onZinsserThinkingStart?.()
                break
              case 'zinsser_thinking':
                callbacks.onZinsserThinkingDelta?.(event.thinking)
                break
              case 'zinsser_thinking_complete':
                callbacks.onZinsserThinkingComplete?.()
                break
              // Zinsser text editor edits
              case 'zinsser_edit':
                callbacks.onZinsserEdit?.(event.old_str, event.new_str)
                break
              case 'zinsser_insert':
                callbacks.onZinsserInsert?.(event.insert_line, event.insert_text)
                break
              // CutClutter-specific events (micro editing - Pass 1)
              case 'cut_clutter_start':
                callbacks.onCutClutterStart?.()
                break
              case 'cut_clutter_thinking_start':
                callbacks.onCutClutterThinkingStart?.()
                break
              case 'cut_clutter_thinking':
                callbacks.onCutClutterThinkingDelta?.(event.thinking)
                break
              case 'cut_clutter_thinking_complete':
                callbacks.onCutClutterThinkingComplete?.()
                break
              // CutClutter text editor edits
              case 'cut_clutter_edit':
                callbacks.onCutClutterEdit?.(event.old_str, event.new_str)
                break
              // StrengthenVerbs-specific events (micro editing - Pass 2)
              case 'strengthen_verbs_start':
                callbacks.onStrengthenVerbsStart?.()
                break
              case 'strengthen_verbs_thinking_start':
                callbacks.onStrengthenVerbsThinkingStart?.()
                break
              case 'strengthen_verbs_thinking':
                callbacks.onStrengthenVerbsThinkingDelta?.(event.thinking)
                break
              case 'strengthen_verbs_thinking_complete':
                callbacks.onStrengthenVerbsThinkingComplete?.()
                break
              // StrengthenVerbs text editor edits
              case 'strengthen_verbs_edit':
                callbacks.onStrengthenVerbsEdit?.(event.old_str, event.new_str)
                break
              // CheckLogic-specific events (micro editing - Pass 3)
              case 'check_logic_start':
                callbacks.onCheckLogicStart?.()
                break
              case 'check_logic_thinking_start':
                callbacks.onCheckLogicThinkingStart?.()
                break
              case 'check_logic_thinking':
                callbacks.onCheckLogicThinkingDelta?.(event.thinking)
                break
              case 'check_logic_thinking_complete':
                callbacks.onCheckLogicThinkingComplete?.()
                break
              // CheckLogic text editor edits
              case 'check_logic_edit':
                callbacks.onCheckLogicEdit?.(event.old_str, event.new_str)
                break
              // RhythmSound-specific events (micro editing - Pass 4)
              case 'rhythm_sound_start':
                callbacks.onRhythmSoundStart?.()
                break
              case 'rhythm_sound_thinking_start':
                callbacks.onRhythmSoundThinkingStart?.()
                break
              case 'rhythm_sound_thinking':
                callbacks.onRhythmSoundThinkingDelta?.(event.thinking)
                break
              case 'rhythm_sound_thinking_complete':
                callbacks.onRhythmSoundThinkingComplete?.()
                break
              // RhythmSound text editor edits
              case 'rhythm_sound_edit':
                callbacks.onRhythmSoundEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_sound_done':
                callbacks.onRhythmSoundDone?.(event.output)
                break
              // Session complete (workflow finished - includes final diff)
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
              // Responder-specific events (coaching response after editing)
              case 'responder_start':
                callbacks.onResponderStart?.()
                break
              case 'responder_thinking_start':
                callbacks.onResponderThinkingStart?.()
                break
              case 'responder_thinking':
                callbacks.onResponderThinkingDelta?.(event.thinking)
                break
              case 'responder_thinking_complete':
                callbacks.onResponderThinkingComplete?.()
                break
              case 'responder_done':
                callbacks.onResponderDone?.(event.output)
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Zinsser Gatekeeper Streaming API
// ═══════════════════════════════════════════════════════════

export async function streamZinsserGatekeeper(
  params: {
    content: string
    session_id: string
    new_doc?: string
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // Gatekeeper thinking (interview phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Gatekeeper done
    onGatekeeperDone?: (output: ZGatekeeperResponse) => void
    // Zinsser start (macro editing phase begins)
    onZinsserStart?: () => void
    // Zinsser thinking (macro editing phase)
    onZinsserThinkingStart?: () => void
    onZinsserThinkingDelta?: (delta: string) => void
    onZinsserThinkingComplete?: () => void
    // Zinsser done
    onZinsserDone?: (output: ZinsserResponse) => void
    // Zinsser text editor edits (streamed as they happen)
    onZinsserEdit?: (oldStr: string, newStr: string) => void
    onZinsserInsert?: (insertLine: number, insertText: string) => void
    // CutClutter (micro editing - Pass 1)
    onCutClutterStart?: () => void
    onCutClutterThinkingStart?: () => void
    onCutClutterThinkingDelta?: (delta: string) => void
    onCutClutterThinkingComplete?: () => void
    // CutClutter done
    onCutClutterDone?: (output: ZinsserResponse) => void
    // CutClutter text editor edits (streamed as they happen)
    onCutClutterEdit?: (oldStr: string, newStr: string) => void
    // StrengthenVerbs (micro editing - Pass 2)
    onStrengthenVerbsStart?: () => void
    onStrengthenVerbsThinkingStart?: () => void
    onStrengthenVerbsThinkingDelta?: (delta: string) => void
    onStrengthenVerbsThinkingComplete?: () => void
    // StrengthenVerbs done
    onStrengthenVerbsDone?: (output: ZinsserResponse) => void
    // StrengthenVerbs text editor edits (streamed as they happen)
    onStrengthenVerbsEdit?: (oldStr: string, newStr: string) => void
    // CheckLogic (micro editing - Pass 3)
    onCheckLogicStart?: () => void
    onCheckLogicThinkingStart?: () => void
    onCheckLogicThinkingDelta?: (delta: string) => void
    onCheckLogicThinkingComplete?: () => void
    // CheckLogic done
    onCheckLogicDone?: (output: ZinsserResponse) => void
    // CheckLogic text editor edits (streamed as they happen)
    onCheckLogicEdit?: (oldStr: string, newStr: string) => void
    // RhythmSound (micro editing - Pass 4)
    onRhythmSoundStart?: () => void
    onRhythmSoundThinkingStart?: () => void
    onRhythmSoundThinkingDelta?: (delta: string) => void
    onRhythmSoundThinkingComplete?: () => void
    // RhythmSound done
    onRhythmSoundDone?: (output: ZinsserResponse) => void
    // RhythmSound text editor edits (streamed as they happen)
    onRhythmSoundEdit?: (oldStr: string, newStr: string) => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Responder (coaching response after editing)
    onResponderStart?: () => void
    onResponderThinkingStart?: () => void
    onResponderThinkingDelta?: (delta: string) => void
    onResponderThinkingComplete?: () => void
    onResponderDone?: (output: ResponderResponse) => void
    // Legacy: called for gatekeeper done (backwards compat)
    onDone: (output: ZGatekeeperResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: params.content,
      session_id: params.session_id,
      new_doc: params.new_doc,
      mode: 'gatekeeper',
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                // Gatekeeper and Zinsser use generic 'done' event
                if (event.output?.zin_doc !== undefined && event.output?.diagnosis !== undefined) {
                  // This is Zinsser output (macro editing) - has zin_doc AND diagnosis
                  callbacks.onZinsserDone?.(event.output)
                } else if (event.output?.response) {
                  // This is Gatekeeper output
                  callbacks.onGatekeeperDone?.(event.output)
                  callbacks.onDone(event.output) // Legacy compat
                }
                break
              case 'cut_clutter_done':
                callbacks.onCutClutterDone?.(event.output)
                break
              case 'strengthen_verbs_done':
                callbacks.onStrengthenVerbsDone?.(event.output)
                break
              case 'check_logic_done':
                callbacks.onCheckLogicDone?.(event.output)
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'zinsser_start':
                callbacks.onZinsserStart?.()
                break
              case 'zinsser_thinking_start':
                callbacks.onZinsserThinkingStart?.()
                break
              case 'zinsser_thinking':
                callbacks.onZinsserThinkingDelta?.(event.thinking)
                break
              case 'zinsser_thinking_complete':
                callbacks.onZinsserThinkingComplete?.()
                break
              case 'zinsser_edit':
                callbacks.onZinsserEdit?.(event.old_str, event.new_str)
                break
              case 'zinsser_insert':
                callbacks.onZinsserInsert?.(event.insert_line, event.insert_text)
                break
              case 'cut_clutter_start':
                callbacks.onCutClutterStart?.()
                break
              case 'cut_clutter_thinking_start':
                callbacks.onCutClutterThinkingStart?.()
                break
              case 'cut_clutter_thinking':
                callbacks.onCutClutterThinkingDelta?.(event.thinking)
                break
              case 'cut_clutter_thinking_complete':
                callbacks.onCutClutterThinkingComplete?.()
                break
              case 'cut_clutter_edit':
                callbacks.onCutClutterEdit?.(event.old_str, event.new_str)
                break
              case 'strengthen_verbs_start':
                callbacks.onStrengthenVerbsStart?.()
                break
              case 'strengthen_verbs_thinking_start':
                callbacks.onStrengthenVerbsThinkingStart?.()
                break
              case 'strengthen_verbs_thinking':
                callbacks.onStrengthenVerbsThinkingDelta?.(event.thinking)
                break
              case 'strengthen_verbs_thinking_complete':
                callbacks.onStrengthenVerbsThinkingComplete?.()
                break
              case 'strengthen_verbs_edit':
                callbacks.onStrengthenVerbsEdit?.(event.old_str, event.new_str)
                break
              case 'check_logic_start':
                callbacks.onCheckLogicStart?.()
                break
              case 'check_logic_thinking_start':
                callbacks.onCheckLogicThinkingStart?.()
                break
              case 'check_logic_thinking':
                callbacks.onCheckLogicThinkingDelta?.(event.thinking)
                break
              case 'check_logic_thinking_complete':
                callbacks.onCheckLogicThinkingComplete?.()
                break
              case 'check_logic_edit':
                callbacks.onCheckLogicEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_sound_start':
                callbacks.onRhythmSoundStart?.()
                break
              case 'rhythm_sound_thinking_start':
                callbacks.onRhythmSoundThinkingStart?.()
                break
              case 'rhythm_sound_thinking':
                callbacks.onRhythmSoundThinkingDelta?.(event.thinking)
                break
              case 'rhythm_sound_thinking_complete':
                callbacks.onRhythmSoundThinkingComplete?.()
                break
              case 'rhythm_sound_edit':
                callbacks.onRhythmSoundEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_sound_done':
                callbacks.onRhythmSoundDone?.(event.output)
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
              case 'responder_start':
                callbacks.onResponderStart?.()
                break
              case 'responder_thinking_start':
                callbacks.onResponderThinkingStart?.()
                break
              case 'responder_thinking':
                callbacks.onResponderThinkingDelta?.(event.thinking)
                break
              case 'responder_thinking_complete':
                callbacks.onResponderThinkingComplete?.()
                break
              case 'responder_done':
                callbacks.onResponderDone?.(event.output)
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Zinsser Cut Clutter Streaming API (Right-Click Context Menu)
// ═══════════════════════════════════════════════════════════

export interface CutClutterResponse {
  zin_doc: string
  changes_made: string[]
}

/**
 * Stream the Cut Clutter workflow for a text selection.
 * Activated via right-click context menu on selected text.
 * Bypasses Gatekeeper and goes directly to clutter removal.
 */
export async function streamCutClutter(
  params: {
    session_id: string
    new_doc: string
    selection_start: number
    selection_end: number
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // CutClutter thinking (editing phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // CutClutter text editor edits (streamed as they happen)
    onEdit?: (oldStr: string, newStr: string) => void
    // CutClutter done
    onDone?: (output: CutClutterResponse) => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Error handler
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: '',  // No chat message for cut_clutter workflow
      session_id: params.session_id,
      new_doc: params.new_doc,
      workflow_mode: 'cut_clutter',  // Selects CutClutterWorkflow
      selection_start: params.selection_start,
      selection_end: params.selection_end,
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'cut_clutter_start':
                // CutClutter node started - no callback needed
                break
              case 'cut_clutter_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'cut_clutter_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'cut_clutter_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'cut_clutter_edit':
                callbacks.onEdit?.(event.old_str, event.new_str)
                break
              case 'cut_clutter_done':
                callbacks.onDone?.(event.output)
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Strengthen Verbs Standalone Workflow API
// ═══════════════════════════════════════════════════════════

/**
 * Stream strengthen verbs response for selected text (bypasses Gatekeeper).
 *
 * This is a focused workflow that targets weak verbs:
 * - Passive voice → active voice
 * - "To be" verbs → specific action verbs
 * - Generic verbs → precise verbs
 * - Adverb-verb combinations → single strong verbs
 * - Nominalized verbs → actual verbs
 */
export async function streamStrengthenVerbs(
  params: {
    session_id: string
    new_doc: string
    selection_start: number
    selection_end: number
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // StrengthenVerbs thinking (editing phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // StrengthenVerbs text editor edits (streamed as they happen)
    onEdit?: (oldStr: string, newStr: string) => void
    // StrengthenVerbs done
    onDone?: () => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Error handler
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: '',  // No chat message for strengthen_verbs workflow
      session_id: params.session_id,
      new_doc: params.new_doc,
      workflow_mode: 'strengthen_verbs',  // Selects StrengthenVerbsWorkflow
      selection_start: params.selection_start,
      selection_end: params.selection_end,
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'strengthen_verbs_start':
                // StrengthenVerbs node started - no callback needed
                break
              case 'strengthen_verbs_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'strengthen_verbs_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'strengthen_verbs_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'strengthen_verbs_edit':
                callbacks.onEdit?.(event.old_str, event.new_str)
                break
              case 'strengthen_verbs_done':
                callbacks.onDone?.()
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Rhythm Standalone Workflow API
// ═══════════════════════════════════════════════════════════

/**
 * Stream rhythm variation response for selected text (bypasses Gatekeeper).
 *
 * This is a focused workflow that targets sentence rhythm:
 * - Varying sentence length (short punchy vs long flowing)
 * - Breaking monotonous patterns
 * - Using fragments strategically for emphasis
 * - Creating natural reading flow
 */
export async function streamRhythm(
  params: {
    session_id: string
    new_doc: string
    selection_start: number
    selection_end: number
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // Rhythm thinking (editing phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Rhythm text editor edits (streamed as they happen)
    onEdit?: (oldStr: string, newStr: string) => void
    // Rhythm done
    onDone?: () => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Error handler
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: '',  // No chat message for rhythm workflow
      session_id: params.session_id,
      new_doc: params.new_doc,
      workflow_mode: 'rhythm',  // Selects RhythmWorkflow
      selection_start: params.selection_start,
      selection_end: params.selection_end,
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'rhythm_start':
                // Rhythm node started - no callback needed
                break
              case 'rhythm_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'rhythm_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'rhythm_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'rhythm_edit':
                callbacks.onEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_done':
                callbacks.onDone?.()
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Zinsser CheckLogic Workflow (Logic checking on selection)
// ═══════════════════════════════════════════════════════════

/**
 * Stream logic checking on user selection.
 *
 * Applies Zinsser's Pass 3 micro-editing focused on:
 * - Paragraph-to-paragraph transitions
 * - Gaps in reasoning
 * - Redundancies in structure
 * - The "So What?" test
 * - Sequence and priority
 *
 * Triggered by right-click context menu "Check Logic".
 */
export async function streamCheckLogic(
  params: {
    session_id: string
    new_doc: string
    selection_start: number
    selection_end: number
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // CheckLogic thinking (editing phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // CheckLogic text editor edits (streamed as they happen)
    onEdit?: (oldStr: string, newStr: string) => void
    // CheckLogic done
    onDone?: () => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Error handler
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: '',  // No chat message for check_logic workflow
      session_id: params.session_id,
      new_doc: params.new_doc,
      workflow_mode: 'check_logic',  // Selects CheckLogicWorkflow
      selection_start: params.selection_start,
      selection_end: params.selection_end,
      run_mode: params.run_mode || 'fast',
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'check_logic_start':
                // CheckLogic node started - no callback needed
                break
              case 'check_logic_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'check_logic_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'check_logic_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'check_logic_edit':
                callbacks.onEdit?.(event.old_str, event.new_str)
                break
              case 'check_logic_done':
                callbacks.onDone?.()
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Zinsser Voice Workflow (Levine Voice - full document transformation)
// ═══════════════════════════════════════════════════════════

/**
 * Stream voice transformation on full document.
 *
 * Unlike other micro-editing workflows (CutClutter, StrengthenVerbs, Rhythm)
 * which operate on selections, Voice operates on the ENTIRE document to apply
 * holistic Levine-style voice transformation.
 *
 * Triggered by toolbar button, not context menu.
 */
export async function streamVoice(
  params: {
    session_id: string
    new_doc: string
    run_mode?: 'fast' | 'deep'
  },
  callbacks: {
    // Voice thinking (editing phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Voice text editor edits (streamed as they happen)
    onEdit?: (oldStr: string, newStr: string) => void
    // Voice done
    onDone?: () => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Error handler
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/zinsser/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: '',  // No chat message for voice workflow
      session_id: params.session_id,
      new_doc: params.new_doc,
      workflow_mode: 'voice',  // Selects VoiceWorkflow
      run_mode: params.run_mode || 'fast',
      // No selection_start/selection_end - voice operates on full document
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'voice_start':
                // Voice node started - no callback needed
                break
              case 'voice_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'voice_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'voice_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'voice_edit':
                callbacks.onEdit?.(event.old_str, event.new_str)
                break
              case 'voice_done':
                callbacks.onDone?.()
                break
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ═══════════════════════════════════════════════════════════
// Z Session Attachments API
// ═══════════════════════════════════════════════════════════

export interface Attachment {
  attachment_id: string
  session_id: string
  filename: string
  original_filename: string
  mime_type: string
  file_size_bytes: number
  file_type: 'text' | 'pdf' | 'image'
  extraction_status: 'pending' | 'completed' | 'failed'
  extraction_error: string | null
  created_at: string
  processed_at: string | null
}

export interface AttachmentListResponse {
  attachments: Attachment[]
  count: number
  max_allowed: number
}

export interface UploadValidationResponse {
  can_upload: boolean
  reason: string | null
  current_count: number
  max_allowed: number
}

export async function uploadAttachment(
  sessionId: string,
  file: File
): Promise<Attachment> {
  const authHeaders = await getAuthHeaders()
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/attachments`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `Failed to upload attachment: ${response.statusText}`)
  }
  return response.json()
}

export async function listAttachments(sessionId: string): Promise<AttachmentListResponse> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/attachments`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list attachments: ${response.statusText}`)
  }
  return response.json()
}

export async function validateUpload(
  sessionId: string,
  fileSize: number,
  mimeType: string
): Promise<UploadValidationResponse> {
  const params = new URLSearchParams({
    file_size: fileSize.toString(),
    mime_type: mimeType,
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/attachments/validate?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to validate upload: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteAttachment(
  sessionId: string,
  attachmentId: string
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/z/sessions/${sessionId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete attachment: ${response.statusText}`)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Zinsser Attachments API
// ═══════════════════════════════════════════════════════════════════════════

export async function uploadZinsserAttachment(
  sessionId: string,
  file: File
): Promise<Attachment> {
  const authHeaders = await getAuthHeaders()
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/attachments`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `Failed to upload attachment: ${response.statusText}`)
  }
  return response.json()
}

export async function listZinsserAttachments(sessionId: string): Promise<AttachmentListResponse> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/attachments`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list attachments: ${response.statusText}`)
  }
  return response.json()
}

export async function validateZinsserUpload(
  sessionId: string,
  fileSize: number,
  mimeType: string
): Promise<UploadValidationResponse> {
  const params = new URLSearchParams({
    file_size: fileSize.toString(),
    mime_type: mimeType,
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/attachments/validate?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to validate upload: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteZinsserAttachment(
  sessionId: string,
  attachmentId: string
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/zinsser/sessions/${sessionId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete attachment: ${response.statusText}`)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Foolscap (F) Attachments API
// ═══════════════════════════════════════════════════════════════════════════

export async function uploadFoolscapAttachment(
  sessionId: string,
  file: File
): Promise<Attachment> {
  const authHeaders = await getAuthHeaders()
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}/attachments`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `Failed to upload attachment: ${response.statusText}`)
  }
  return response.json()
}

export async function listFoolscapAttachments(sessionId: string): Promise<AttachmentListResponse> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}/attachments`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list attachments: ${response.statusText}`)
  }
  return response.json()
}

export async function validateFoolscapUpload(
  sessionId: string,
  fileSize: number,
  mimeType: string
): Promise<UploadValidationResponse> {
  const params = new URLSearchParams({
    file_size: fileSize.toString(),
    mime_type: mimeType,
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}/attachments/validate?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to validate upload: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteFoolscapAttachment(
  sessionId: string,
  attachmentId: string
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete attachment: ${response.statusText}`)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// User Profile / Approval Status API
// ═══════════════════════════════════════════════════════════════════════════

export type ProfileStatus = 'pending' | 'approved' | 'rejected'

export interface UserProfile {
  id: string
  email: string | null
  full_name: string | null
  status: ProfileStatus
  email_verified: boolean
  created_at: string
  updated_at: string
  approved_at: string | null
  approved_by: string | null
  levine_voice_enabled: boolean
}

/**
 * Fetch the current user's profile from Supabase.
 * Returns null if no profile exists (user needs to be created).
 */
export async function fetchUserProfile(): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .single()

  if (error) {
    // PGRST116 = no rows returned (profile doesn't exist yet)
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(`Failed to fetch profile: ${error.message}`)
  }

  return data as UserProfile
}

// =============================================================================
// CP (Commonplace) Session API
// =============================================================================

export interface CpSessionListItem {
  session_id: string
  name: string
  mode: string
  status: 'active' | 'archived'
  total_turns: number
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface CpSessionTurn {
  turn_id: string
  session_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: string
  thinking: string | null
  timestamp: string
}

export interface CpSession extends CpSessionListItem {
  brainstorm_content: string | null
  foolscap_content: string | null
  session_context: Record<string, unknown> | null
}

export interface CpSessionWithTurns extends CpSession {
  turns: CpSessionTurn[]
}

export async function listCpSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<CpSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

export async function createCpSession(
  name?: string,
  mode = 'chat'
): Promise<CpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ name, mode }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

export async function getCpSession(sessionId: string): Promise<CpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function getCpSessionWithTurns(sessionId: string): Promise<CpSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session with turns: ${response.statusText}`)
  }
  return response.json()
}

export async function updateCpSession(
  sessionId: string,
  updates: {
    name?: string
    mode?: string
    status?: 'active' | 'archived'
  }
): Promise<CpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteCpSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

// =============================================================================
// CP Streaming API
// =============================================================================

export interface CpChatResponse {
  response: string
  thinking?: string
  session_id: string
  retrieved_cards?: RetrievedCard[]
}

export interface CpCardIngestionEvent {
  type: string
  // Ingest
  source_id?: string
  content_length?: number
  // Cardify
  card?: {
    type: string
    domain: string
    phase: string
    title: string
    summary: string
    tags: string[]
    confidence: number
  }
  current?: number
  total?: number
  card_count?: number
  // Canonicalize
  decision?: string
  // Link
  edge?: {
    from_card_id: string
    to_card_id: string
    type: string
    weight: number
    is_new: boolean
  }
  // Summary
  new_count?: number
  duplicate_count?: number
  related_count?: number
  edges_created?: number
  card_ids?: string[]
  // Error
  error?: string
  message?: string
}

/**
 * Stream CP chat response with thinking and card context
 */
export async function streamCpChat(
  params: {
    content: string
    session_id: string
    images?: Array<{ data: string; media_type: string }>
  },
  callbacks: {
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    onResponseDelta?: (delta: string) => void
    onDone?: (output: CpChatResponse) => void
    onRetrievedCards?: (cards: RetrievedCard[]) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/cp/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') continue

        try {
          const event = JSON.parse(data)

          switch (event.type) {
            case 'thinking_start':
              callbacks.onThinkingStart?.()
              break
            case 'thinking':
              callbacks.onThinkingDelta?.(event.content || '')
              break
            case 'thinking_complete':
              callbacks.onThinkingComplete?.()
              break
            case 'response':
              callbacks.onResponseDelta?.(event.content || '')
              break
            case 'retrieved_cards':
              callbacks.onRetrievedCards?.(event.cards || [])
              break
            case 'done':
              callbacks.onDone?.(event.output || event)
              break
            case 'error':
              callbacks.onError(event.message || 'Unknown error')
              break
          }
        } catch (e) {
          console.error('[streamCpChat] Failed to parse event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Stream CP card ingestion with progress events
 * Supports multiple images + text + attachment simultaneously
 */
export async function streamCpCardIngestion(
  params: {
    session_id?: string
    raw_text?: string
    domain_hint?: string
    tags?: string[]
    title?: string
    images?: Array<{ data: string; mimeType: string; name: string }>
  },
  file: File | null,
  callbacks: {
    onExtractionStart?: () => void
    onExtractionComplete?: () => void
    onIngestStart?: () => void
    onIngestComplete?: (sourceId: string) => void
    onDuplicateDetected?: (sourceId: string) => void
    onCardifyStart?: () => void
    onCardProposed?: (card: CpCardIngestionEvent['card'], current: number, total: number) => void
    onCardifyComplete?: (cardCount: number) => void
    onEmbedStart?: () => void
    onEmbedComplete?: () => void
    onCanonicalizeStart?: () => void
    onCanonicalizeDecision?: (card: unknown, current: number, total: number) => void
    onCanonicalizeComplete?: (newCount: number, duplicateCount: number, relatedCount: number) => void
    onLinkStart?: () => void
    onLinkCreated?: (edge: CpCardIngestionEvent['edge']) => void
    onLinkComplete?: (edgesCreated: number) => void
    onDone?: (summary: { source_id: string; cards_created: number; cards_duplicate: number; edges_created: number; card_ids: string[] }) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()

  // Build form data
  const formData = new FormData()
  if (params.session_id) formData.append('session_id', params.session_id)
  if (params.raw_text) formData.append('raw_text', params.raw_text)
  if (params.domain_hint) formData.append('domain_hint', params.domain_hint)
  if (params.tags) formData.append('tags', params.tags.join(','))
  if (params.title) formData.append('title', params.title)
  if (file) formData.append('attachment', file)

  // Append multiple images (convert base64 to File objects)
  if (params.images && params.images.length > 0) {
    for (const img of params.images) {
      const byteString = atob(img.data)
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: img.mimeType })
      const imageFile = new File([blob], img.name, { type: img.mimeType })
      formData.append('images', imageFile)
    }
  }

  const response = await fetch(`${API_BASE}/cp/card/stream`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') continue

        try {
          const event: CpCardIngestionEvent = JSON.parse(data)

          switch (event.type) {
            // Extraction events (for images)
            case 'extraction_start':
              callbacks.onExtractionStart?.()
              break
            case 'extraction_complete':
              callbacks.onExtractionComplete?.()
              break

            // Ingest events
            case 'ingest_start':
              callbacks.onIngestStart?.()
              break
            case 'ingest_complete':
              callbacks.onIngestComplete?.(event.source_id || '')
              break
            case 'duplicate_detected':
              callbacks.onDuplicateDetected?.(event.source_id || '')
              break

            // Cardify events
            case 'cardify_start':
              callbacks.onCardifyStart?.()
              break
            case 'card_proposed':
              callbacks.onCardProposed?.(event.card, event.current || 0, event.total || 0)
              break
            case 'cardify_complete':
              callbacks.onCardifyComplete?.(event.card_count || 0)
              break

            // Embed events
            case 'embed_start':
              callbacks.onEmbedStart?.()
              break
            case 'embed_complete':
              callbacks.onEmbedComplete?.()
              break

            // Canonicalize events
            case 'canonicalize_start':
              callbacks.onCanonicalizeStart?.()
              break
            case 'canonicalize_decision':
              callbacks.onCanonicalizeDecision?.(event.card, event.current || 0, event.total || 0)
              break
            case 'canonicalize_complete':
              callbacks.onCanonicalizeComplete?.(
                event.new_count || 0,
                event.duplicate_count || 0,
                event.related_count || 0
              )
              break

            // Link events
            case 'link_start':
              callbacks.onLinkStart?.()
              break
            case 'link_created':
              callbacks.onLinkCreated?.(event.edge)
              break
            case 'link_complete':
              callbacks.onLinkComplete?.(event.edges_created || 0)
              break

            // Done
            case 'done':
              // cards_created = new + related (both result in actual new cards)
              callbacks.onDone?.({
                source_id: event.source_id || '',
                cards_created: (event.new_count || 0) + (event.related_count || 0),
                cards_duplicate: event.duplicate_count || 0,
                edges_created: event.edges_created || 0,
                card_ids: event.card_ids || [],
              })
              break

            // Error
            case 'error':
              callbacks.onError(event.message || event.error || 'Unknown error')
              break
          }
        } catch (e) {
          console.error('[streamCpCardIngestion] Failed to parse event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// =============================================================================
// D (Drafter) Session API
// =============================================================================

export type DMode = 'gatekeeper' | 'draft'

export interface VoiceDirection {
  hook_type?: 'deadpan_inversion' | 'permission_statement' | 'systemic_accusation'
  primary_mode?: 'deadpan' | 'earnest'
  framework_type?: 'numbered_set' | 'named_model' | 'dichotomy' | 'spectrum'
  kicker_style?: 'absurdist' | 'philosophical' | 'call_to_becoming' | 'dropped_mic'
  target_reader?: string
}

export interface FoolscapSummary {
  session_id: string
  name: string | null
  entity_name: string | null
  foolscap_markdown: string | null
  created_at: string
  updated_at: string
}

export interface DSessionListItem {
  session_id: string
  name: string | null
  status: 'active' | 'archived'
  total_turns: number
  source_foolscap_id: string | null
  created_at: string
  last_message_at: string | null
}

export interface DSessionTurn {
  turn_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  thinking?: string
  timestamp: string
}

export interface DSession extends DSessionListItem {
  foolscap_snapshot: string | null
  foolscap_json_snapshot: Record<string, unknown> | null
  new_doc: string | null
  old_doc: string | null
  zin_doc: string | null
  voice_direction: VoiceDirection | null
  session_context: Record<string, unknown> | null
  updated_at: string
}

export interface DSessionWithTurns extends DSession {
  turns: DSessionTurn[]
}

// List available completed Foolscaps for drafting
export async function listAvailableFoolscaps(limit = 50): Promise<FoolscapSummary[]> {
  const params = new URLSearchParams({ limit: limit.toString() })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/foolscaps?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list foolscaps: ${response.statusText}`)
  }
  return response.json()
}

// List D sessions
export async function listDSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<DSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list D sessions: ${response.statusText}`)
  }
  return response.json()
}

// Drafter spec for configuring draft output
export interface DrafterSpec {
  piece_type: 'essay' | 'article' | 'newsletter' | 'thread' | 'script' | 'memo' | 'letter' | 'deck'
  target_length: number // in words
}

// Create a new D session from a Foolscap
export async function createDSession(
  sourceFoolscapId: string,
  name?: string,
  drafterSpec?: DrafterSpec
): Promise<DSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      source_foolscap_id: sourceFoolscapId,
      name,
      drafter_spec: drafterSpec,
    }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create D session: ${response.statusText}`)
  }
  return response.json()
}

// Get a D session
export async function getDSession(sessionId: string): Promise<DSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get D session: ${response.statusText}`)
  }
  return response.json()
}

// Get a D session with all turns
export async function getDSessionWithTurns(sessionId: string): Promise<DSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get D session with turns: ${response.statusText}`)
  }
  return response.json()
}

// Update a D session
export async function updateDSession(
  sessionId: string,
  updates: {
    name?: string
    status?: 'active' | 'archived'
    new_doc?: string
    old_doc?: string
    zin_doc?: string
    voice_direction?: VoiceDirection
    session_context?: Record<string, unknown>
  }
): Promise<DSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update D session: ${response.statusText}`)
  }
  return response.json()
}

// Delete a D session
export async function deleteDSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete D session: ${response.statusText}`)
  }
}

// Get D session turns
export async function getDSessionTurns(
  sessionId: string,
  limit?: number
): Promise<DSessionTurn[]> {
  const params = limit ? `?limit=${limit}` : ''
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/d/sessions/${sessionId}/turns${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get D session turns: ${response.statusText}`)
  }
  return response.json()
}

// ═══════════════════════════════════════════════════════════
// D Workflow Streaming API
// ═══════════════════════════════════════════════════════════

export interface DGatekeeperResponse {
  response: string
  session_id?: string
  thinking?: string
}

export async function streamDGatekeeper(
  params: {
    content: string
    session_id: string
    new_doc?: string
    foolscap_snapshot?: string
  },
  callbacks: {
    // Gatekeeper thinking (interview phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Gatekeeper done
    onGatekeeperDone?: (output: DGatekeeperResponse) => void
    // Zinsser start (macro editing phase begins)
    onZinsserStart?: () => void
    // Zinsser thinking (macro editing phase)
    onZinsserThinkingStart?: () => void
    onZinsserThinkingDelta?: (delta: string) => void
    onZinsserThinkingComplete?: () => void
    // Zinsser done
    onZinsserDone?: (output: ZinsserResponse) => void
    // Zinsser text editor edits (streamed as they happen)
    onZinsserEdit?: (oldStr: string, newStr: string) => void
    onZinsserInsert?: (insertLine: number, insertText: string) => void
    // StrengthenVerbs (micro editing - Pass 1) - D workflow skips CutClutter
    onStrengthenVerbsStart?: () => void
    onStrengthenVerbsThinkingStart?: () => void
    onStrengthenVerbsThinkingDelta?: (delta: string) => void
    onStrengthenVerbsThinkingComplete?: () => void
    // StrengthenVerbs done
    onStrengthenVerbsDone?: (output: ZinsserResponse) => void
    // StrengthenVerbs text editor edits (streamed as they happen)
    onStrengthenVerbsEdit?: (oldStr: string, newStr: string) => void
    // CheckLogic (micro editing - Pass 2)
    onCheckLogicStart?: () => void
    onCheckLogicThinkingStart?: () => void
    onCheckLogicThinkingDelta?: (delta: string) => void
    onCheckLogicThinkingComplete?: () => void
    // CheckLogic done
    onCheckLogicDone?: (output: ZinsserResponse) => void
    // CheckLogic text editor edits (streamed as they happen)
    onCheckLogicEdit?: (oldStr: string, newStr: string) => void
    // RhythmSound (micro editing - Pass 3)
    onRhythmSoundStart?: () => void
    onRhythmSoundThinkingStart?: () => void
    onRhythmSoundThinkingDelta?: (delta: string) => void
    onRhythmSoundThinkingComplete?: () => void
    // RhythmSound done
    onRhythmSoundDone?: (output: ZinsserResponse) => void
    // RhythmSound text editor edits (streamed as they happen)
    onRhythmSoundEdit?: (oldStr: string, newStr: string) => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Responder (coaching response after editing)
    onResponderStart?: () => void
    onResponderThinkingStart?: () => void
    onResponderThinkingDelta?: (delta: string) => void
    onResponderThinkingComplete?: () => void
    onResponderDone?: (output: ResponderResponse) => void
    // Legacy: called for gatekeeper done (backwards compat)
    onDone: (output: DGatekeeperResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/d/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: params.content,
      session_id: params.session_id,
      new_doc: params.new_doc,
      foolscap_snapshot: params.foolscap_snapshot,
      mode: 'draft',  // D workflow always uses draft mode (no Gatekeeper phase)
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/d/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              case 'done':
                // Gatekeeper and Zinsser use generic 'done' event
                if (event.output?.zin_doc !== undefined && event.output?.diagnosis !== undefined) {
                  // This is Zinsser output (macro editing) - has zin_doc AND diagnosis
                  callbacks.onZinsserDone?.(event.output)
                } else if (event.output?.response) {
                  // This is Gatekeeper output
                  callbacks.onGatekeeperDone?.(event.output)
                  callbacks.onDone(event.output) // Legacy compat
                }
                break
              case 'strengthen_verbs_done':
                callbacks.onStrengthenVerbsDone?.(event.output)
                break
              case 'check_logic_done':
                callbacks.onCheckLogicDone?.(event.output)
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              // Zinsser-specific events
              case 'zinsser_start':
                callbacks.onZinsserStart?.()
                break
              case 'zinsser_thinking_start':
                callbacks.onZinsserThinkingStart?.()
                break
              case 'zinsser_thinking':
                callbacks.onZinsserThinkingDelta?.(event.thinking)
                break
              case 'zinsser_thinking_complete':
                callbacks.onZinsserThinkingComplete?.()
                break
              // Zinsser text editor edits
              case 'zinsser_edit':
                callbacks.onZinsserEdit?.(event.old_str, event.new_str)
                break
              case 'zinsser_insert':
                callbacks.onZinsserInsert?.(event.insert_line, event.insert_text)
                break
              // StrengthenVerbs-specific events (micro editing - Pass 1)
              case 'strengthen_verbs_start':
                callbacks.onStrengthenVerbsStart?.()
                break
              case 'strengthen_verbs_thinking_start':
                callbacks.onStrengthenVerbsThinkingStart?.()
                break
              case 'strengthen_verbs_thinking':
                callbacks.onStrengthenVerbsThinkingDelta?.(event.thinking)
                break
              case 'strengthen_verbs_thinking_complete':
                callbacks.onStrengthenVerbsThinkingComplete?.()
                break
              // StrengthenVerbs text editor edits
              case 'strengthen_verbs_edit':
                callbacks.onStrengthenVerbsEdit?.(event.old_str, event.new_str)
                break
              // CheckLogic-specific events (micro editing - Pass 2)
              case 'check_logic_start':
                callbacks.onCheckLogicStart?.()
                break
              case 'check_logic_thinking_start':
                callbacks.onCheckLogicThinkingStart?.()
                break
              case 'check_logic_thinking':
                callbacks.onCheckLogicThinkingDelta?.(event.thinking)
                break
              case 'check_logic_thinking_complete':
                callbacks.onCheckLogicThinkingComplete?.()
                break
              // CheckLogic text editor edits
              case 'check_logic_edit':
                callbacks.onCheckLogicEdit?.(event.old_str, event.new_str)
                break
              // RhythmSound-specific events (micro editing - Pass 3)
              case 'rhythm_sound_start':
                callbacks.onRhythmSoundStart?.()
                break
              case 'rhythm_sound_thinking_start':
                callbacks.onRhythmSoundThinkingStart?.()
                break
              case 'rhythm_sound_thinking':
                callbacks.onRhythmSoundThinkingDelta?.(event.thinking)
                break
              case 'rhythm_sound_thinking_complete':
                callbacks.onRhythmSoundThinkingComplete?.()
                break
              // RhythmSound text editor edits
              case 'rhythm_sound_edit':
                callbacks.onRhythmSoundEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_sound_done':
                callbacks.onRhythmSoundDone?.(event.output)
                break
              // Session complete (workflow finished - includes final diff)
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
              // Responder-specific events (coaching response after editing)
              case 'responder_start':
                callbacks.onResponderStart?.()
                break
              case 'responder_thinking_start':
                callbacks.onResponderThinkingStart?.()
                break
              case 'responder_thinking':
                callbacks.onResponderThinkingDelta?.(event.thinking)
                break
              case 'responder_thinking_complete':
                callbacks.onResponderThinkingComplete?.()
                break
              case 'responder_done':
                callbacks.onResponderDone?.(event.output)
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// =============================================================================
// Content CP (Content Commonplace) Session API
// =============================================================================

export interface ContentCpSessionListItem {
  session_id: string
  name: string
  mode: string
  status: 'active' | 'archived'
  total_turns: number
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface ContentCpSessionTurn {
  turn_id: string
  session_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: string
  thinking: string | null
  timestamp: string
}

export interface ContentCpSession extends ContentCpSessionListItem {
  brainstorm_content: string | null
  foolscap_content: string | null
  session_context: Record<string, unknown> | null
}

export interface ContentCpSessionWithTurns extends ContentCpSession {
  turns: ContentCpSessionTurn[]
}

export async function listContentCpSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<ContentCpSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

export async function createContentCpSession(
  name?: string,
  mode = 'chat'
): Promise<ContentCpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ name, mode }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

export async function getContentCpSession(sessionId: string): Promise<ContentCpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function getContentCpSessionWithTurns(sessionId: string): Promise<ContentCpSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session with turns: ${response.statusText}`)
  }
  return response.json()
}

export async function updateContentCpSession(
  sessionId: string,
  updates: {
    name?: string
    mode?: string
    status?: 'active' | 'archived'
  }
): Promise<ContentCpSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

export async function deleteContentCpSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

// =============================================================================
// Content CP Session-Scoped Cards API
// =============================================================================

export interface ContentCardSummary {
  card_id: string
  type: string
  domain: string | null
  title: string | null
  summary: string
  tags: string[]
  support_count: number
  created_at: string
}

export interface ContentCardDetail {
  card_id: string
  session_id: string
  type: string
  domain: string | null
  title: string | null
  summary: string
  tags: string[]
  quality_score: number
  support_count: number
  status: string
  created_at: string
  sources: Array<{
    source_id: string
    title: string | null
    filename: string | null
    domain_hint: string | null
    ingested_at: string | null
    role: string
  }>
  edges_from: Array<{
    edge_id: string
    to_card_id: string
    type: string
    weight: number
    to_title: string | null
    to_summary: string
  }>
  edges_to: Array<{
    edge_id: string
    from_card_id: string
    type: string
    weight: number
    from_title: string | null
    from_summary: string
  }>
}

export interface ContentCardsResponse {
  cards: ContentCardSummary[]
  total: number
}

export async function fetchSessionCards(
  sessionId: string,
  params: {
    search?: string
    domain?: string
    type?: string
    page?: number
    page_size?: number
  } = {}
): Promise<ContentCardsResponse> {
  const authHeaders = await getAuthHeaders()
  const searchParams = new URLSearchParams()
  if (params.search) searchParams.set('search', params.search)
  if (params.domain) searchParams.set('domain', params.domain)
  if (params.type) searchParams.set('type', params.type)
  if (params.page) searchParams.set('page', params.page.toString())
  if (params.page_size) searchParams.set('page_size', params.page_size.toString())

  const queryString = searchParams.toString()
  const url = `${API_BASE}/content_cp/sessions/${sessionId}/cards${queryString ? '?' + queryString : ''}`

  const response = await fetch(url, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to fetch session cards: ${response.statusText}`)
  }
  return response.json()
}

export async function fetchSessionCard(
  sessionId: string,
  cardId: string
): Promise<ContentCardDetail> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/sessions/${sessionId}/cards/${cardId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    if (response.status === 404) {
      throw new Error('Card not found')
    }
    throw new Error(`Failed to fetch session card: ${response.statusText}`)
  }
  return response.json()
}

// =============================================================================
// Content CP Streaming API
// =============================================================================

export interface ContentCpChatResponse {
  response: string
  thinking?: string
  session_id: string
  retrieved_cards?: RetrievedCard[]
}

export interface ContentCpCardIngestionEvent {
  type: string
  source_id?: string
  content_length?: number
  card?: {
    type: string
    domain: string
    phase: string
    title: string
    summary: string
    tags: string[]
    confidence: number
  }
  current?: number
  total?: number
  card_count?: number
  decision?: string
  edge?: {
    from_card_id: string
    to_card_id: string
    type: string
    weight: number
    is_new: boolean
  }
  new_count?: number
  duplicate_count?: number
  related_count?: number
  edges_created?: number
  card_ids?: string[]
  error?: string
  message?: string
}

export async function streamContentCpChat(
  params: {
    content: string
    session_id: string
    images?: Array<{ data: string; media_type: string }>
  },
  callbacks: {
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    onResponseDelta?: (delta: string) => void
    onDone?: (output: ContentCpChatResponse) => void
    onRetrievedCards?: (cards: RetrievedCard[]) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_cp/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') continue

        try {
          const event = JSON.parse(data)

          switch (event.type) {
            case 'thinking_start':
              callbacks.onThinkingStart?.()
              break
            case 'thinking':
              callbacks.onThinkingDelta?.(event.content || '')
              break
            case 'thinking_complete':
              callbacks.onThinkingComplete?.()
              break
            case 'response':
              callbacks.onResponseDelta?.(event.content || '')
              break
            case 'retrieved_cards':
              callbacks.onRetrievedCards?.(event.cards || [])
              break
            case 'done':
              callbacks.onDone?.(event.output || event)
              break
            case 'error':
              callbacks.onError(event.message || 'Unknown error')
              break
          }
        } catch (e) {
          console.error('[streamContentCpChat] Failed to parse event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export async function streamContentCpCardIngestion(
  params: {
    session_id?: string
    raw_text?: string
    domain_hint?: string
    tags?: string[]
    title?: string
    images?: Array<{ data: string; mimeType: string; name: string }>
  },
  file: File | null,
  callbacks: {
    onExtractionStart?: () => void
    onExtractionComplete?: () => void
    onIngestStart?: () => void
    onIngestComplete?: (sourceId: string) => void
    onDuplicateDetected?: (sourceId: string) => void
    onCardifyStart?: () => void
    onCardProposed?: (card: ContentCpCardIngestionEvent['card'], current: number, total: number) => void
    onCardifyComplete?: (cardCount: number) => void
    onEmbedStart?: () => void
    onEmbedComplete?: () => void
    onCanonicalizeStart?: () => void
    onCanonicalizeDecision?: (card: unknown, current: number, total: number) => void
    onCanonicalizeComplete?: (newCount: number, duplicateCount: number, relatedCount: number) => void
    onLinkStart?: () => void
    onLinkCreated?: (edge: ContentCpCardIngestionEvent['edge']) => void
    onLinkComplete?: (edgesCreated: number) => void
    onDone?: (summary: { source_id: string; cards_created: number; cards_duplicate: number; edges_created: number; card_ids: string[] }) => void
    onError: (error: string) => void
  }
): Promise<void> {
  const authHeaders = await getAuthHeaders()

  const formData = new FormData()
  if (params.session_id) formData.append('session_id', params.session_id)
  if (params.raw_text) formData.append('raw_text', params.raw_text)
  if (params.domain_hint) formData.append('domain_hint', params.domain_hint)
  if (params.tags) formData.append('tags', params.tags.join(','))
  if (params.title) formData.append('title', params.title)
  if (file) formData.append('attachment', file)

  if (params.images && params.images.length > 0) {
    for (const img of params.images) {
      const byteString = atob(img.data)
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: img.mimeType })
      const imageFile = new File([blob], img.name, { type: img.mimeType })
      formData.append('images', imageFile)
    }
  }

  const response = await fetch(`${API_BASE}/content_cp/card/stream`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') continue

        try {
          const event: ContentCpCardIngestionEvent = JSON.parse(data)

          switch (event.type) {
            case 'extraction_start':
              callbacks.onExtractionStart?.()
              break
            case 'extraction_complete':
              callbacks.onExtractionComplete?.()
              break
            case 'ingest_start':
              callbacks.onIngestStart?.()
              break
            case 'ingest_complete':
              callbacks.onIngestComplete?.(event.source_id || '')
              break
            case 'duplicate_detected':
              callbacks.onDuplicateDetected?.(event.source_id || '')
              break
            case 'cardify_start':
              callbacks.onCardifyStart?.()
              break
            case 'card_proposed':
              callbacks.onCardProposed?.(event.card, event.current || 0, event.total || 0)
              break
            case 'cardify_complete':
              callbacks.onCardifyComplete?.(event.card_count || 0)
              break
            case 'embed_start':
              callbacks.onEmbedStart?.()
              break
            case 'embed_complete':
              callbacks.onEmbedComplete?.()
              break
            case 'canonicalize_start':
              callbacks.onCanonicalizeStart?.()
              break
            case 'canonicalize_decision':
              callbacks.onCanonicalizeDecision?.(event.card, event.current || 0, event.total || 0)
              break
            case 'canonicalize_complete':
              callbacks.onCanonicalizeComplete?.(
                event.new_count || 0,
                event.duplicate_count || 0,
                event.related_count || 0
              )
              break
            case 'link_start':
              callbacks.onLinkStart?.()
              break
            case 'link_created':
              callbacks.onLinkCreated?.(event.edge)
              break
            case 'link_complete':
              callbacks.onLinkComplete?.(event.edges_created || 0)
              break
            case 'done':
              callbacks.onDone?.({
                source_id: event.source_id || '',
                cards_created: event.new_count || 0,
                cards_duplicate: event.duplicate_count || 0,
                edges_created: event.edges_created || 0,
                card_ids: event.card_ids || [],
              })
              break
            case 'error':
              callbacks.onError(event.message || event.error || 'Unknown error')
              break
          }
        } catch (e) {
          console.error('[streamContentCpCardIngestion] Failed to parse event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// =============================================================================
// Content D (Content Drafter) Session API
// =============================================================================

export type ContentDMode = 'gatekeeper' | 'draft'

export interface ContentDVoiceDirection {
  hook_type?: 'deadpan_inversion' | 'permission_statement' | 'systemic_accusation'
  primary_mode?: 'deadpan' | 'earnest'
  framework_type?: 'numbered_set' | 'named_model' | 'dichotomy' | 'spectrum'
  kicker_style?: 'absurdist' | 'philosophical' | 'call_to_becoming' | 'dropped_mic'
  target_reader?: string
}

export interface ContentFoolscapSummary {
  session_id: string
  name: string | null
  entity_name: string | null
  foolscap_markdown: string | null
  created_at: string
  updated_at: string
}

export interface ContentDSessionListItem {
  session_id: string
  name: string | null
  status: 'active' | 'archived'
  total_turns: number
  source_content_f_id: string | null
  created_at: string
  last_message_at: string | null
}

export interface ContentDSessionTurn {
  turn_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  thinking?: string
  timestamp: string
}

export interface ContentDSession extends ContentDSessionListItem {
  foolscap_snapshot: string | null  // Backend field name
  content_foolscap_snapshot?: string | null  // Alias for frontend compatibility
  foolscap_json_snapshot: Record<string, unknown> | null
  new_doc: string | null
  old_doc: string | null
  zin_doc: string | null
  voice_direction: ContentDVoiceDirection | null
  session_context: Record<string, unknown> | null
  updated_at: string
}

export interface ContentDSessionWithTurns extends ContentDSession {
  turns: ContentDSessionTurn[]
}

// List available completed Content Foolscaps for drafting
export async function listAvailableContentFoolscaps(limit = 50): Promise<ContentFoolscapSummary[]> {
  const params = new URLSearchParams({ limit: limit.toString() })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/content_foolscaps?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list content foolscaps: ${response.statusText}`)
  }
  return response.json()
}

// List Content D sessions
export async function listContentDSessions(
  status: 'active' | 'archived' = 'active',
  limit = 50,
  offset = 0
): Promise<ContentDSessionListItem[]> {
  const params = new URLSearchParams({
    status,
    limit: limit.toString(),
    offset: offset.toString(),
  })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions?${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list Content D sessions: ${response.statusText}`)
  }
  return response.json()
}

// Create a new Content D session from a Content Foolscap
// Note: drafter_spec removed - Gatekeeper gathers document preferences conversationally
export async function createContentDSession(
  sourceContentFId: string,
  name?: string
): Promise<ContentDSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      source_content_f_id: sourceContentFId,
      name,
    }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create Content D session: ${response.statusText}`)
  }
  return response.json()
}

// Get a Content D session
export async function getContentDSession(sessionId: string): Promise<ContentDSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/${sessionId}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get Content D session: ${response.statusText}`)
  }
  return response.json()
}

// Get a Content D session with all turns
export async function getContentDSessionWithTurns(sessionId: string): Promise<ContentDSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/${sessionId}/full`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get Content D session with turns: ${response.statusText}`)
  }
  return response.json()
}

// Update a Content D session
export async function updateContentDSession(
  sessionId: string,
  updates: {
    name?: string
    status?: 'active' | 'archived'
    new_doc?: string
    old_doc?: string
    zin_doc?: string
    voice_direction?: ContentDVoiceDirection
    session_context?: Record<string, unknown>
  }
): Promise<ContentDSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update Content D session: ${response.statusText}`)
  }
  return response.json()
}

// Delete a Content D session
export async function deleteContentDSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete Content D session: ${response.statusText}`)
  }
}

// Get Content D session turns
export async function getContentDSessionTurns(
  sessionId: string,
  limit?: number
): Promise<ContentDSessionTurn[]> {
  const params = limit ? `?limit=${limit}` : ''
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/content_d/sessions/${sessionId}/turns${params}`, {
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get Content D session turns: ${response.statusText}`)
  }
  return response.json()
}

// ═══════════════════════════════════════════════════════════
// Content D Workflow Streaming API
// ═══════════════════════════════════════════════════════════

export interface ContentDGatekeeperResponse {
  response: string
  session_id?: string
  thinking?: string
}

export async function streamContentDWorkflow(
  params: {
    content: string
    session_id: string
    new_doc?: string
    content_foolscap_snapshot?: string
  },
  callbacks: {
    // Gatekeeper thinking (interview phase)
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Gatekeeper done
    onGatekeeperDone?: (output: ContentDGatekeeperResponse) => void
    // Zinsser start (macro editing phase begins)
    onZinsserStart?: () => void
    // Zinsser thinking (macro editing phase)
    onZinsserThinkingStart?: () => void
    onZinsserThinkingDelta?: (delta: string) => void
    onZinsserThinkingComplete?: () => void
    // Zinsser done
    onZinsserDone?: (output: ZinsserResponse) => void
    // Zinsser text editor edits (streamed as they happen)
    onZinsserEdit?: (oldStr: string, newStr: string) => void
    onZinsserInsert?: (insertLine: number, insertText: string) => void
    // StrengthenVerbs (micro editing - Pass 1) - Content D workflow skips CutClutter
    onStrengthenVerbsStart?: () => void
    onStrengthenVerbsThinkingStart?: () => void
    onStrengthenVerbsThinkingDelta?: (delta: string) => void
    onStrengthenVerbsThinkingComplete?: () => void
    // StrengthenVerbs done
    onStrengthenVerbsDone?: (output: ZinsserResponse) => void
    // StrengthenVerbs text editor edits (streamed as they happen)
    onStrengthenVerbsEdit?: (oldStr: string, newStr: string) => void
    // CheckLogic (micro editing - Pass 2)
    onCheckLogicStart?: () => void
    onCheckLogicThinkingStart?: () => void
    onCheckLogicThinkingDelta?: (delta: string) => void
    onCheckLogicThinkingComplete?: () => void
    // CheckLogic done
    onCheckLogicDone?: (output: ZinsserResponse) => void
    // CheckLogic text editor edits (streamed as they happen)
    onCheckLogicEdit?: (oldStr: string, newStr: string) => void
    // RhythmSound (micro editing - Pass 3)
    onRhythmSoundStart?: () => void
    onRhythmSoundThinkingStart?: () => void
    onRhythmSoundThinkingDelta?: (delta: string) => void
    onRhythmSoundThinkingComplete?: () => void
    // RhythmSound done
    onRhythmSoundDone?: (output: ZinsserResponse) => void
    // RhythmSound text editor edits (streamed as they happen)
    onRhythmSoundEdit?: (oldStr: string, newStr: string) => void
    // Session complete (workflow finished - includes final diff)
    onSessionComplete?: (output: SessionCompleteOutput) => void
    // Responder (coaching response after editing)
    onResponderStart?: () => void
    onResponderThinkingStart?: () => void
    onResponderThinkingDelta?: (delta: string) => void
    onResponderThinkingComplete?: () => void
    onResponderDone?: (output: ResponderResponse) => void
    // Legacy: called for gatekeeper done (backwards compat)
    onDone: (output: ContentDGatekeeperResponse) => void
    onError: (error: string) => void
  }
): Promise<void> {
  // CONTENT D STREAMING HANDLER
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/content_d/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      content: params.content,
      session_id: params.session_id,
      new_doc: params.new_doc,
      foolscap_snapshot: params.content_foolscap_snapshot,  // Backend expects foolscap_snapshot
      mode: 'gatekeeper',  // Content D workflow uses gatekeeper for multi-turn conversation
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/content_d/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)

        if (data === '[DONE]') {
          return
        }

        try {
          const event = JSON.parse(data)

          if ('type' in event) {
            switch (event.type) {
              // Gatekeeper-specific events (Content D emits these)
              case 'gatekeeper_thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'gatekeeper_thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'gatekeeper_thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              case 'gatekeeper_done':
                callbacks.onGatekeeperDone?.(event.output)
                callbacks.onDone(event.output)
                break
              case 'done':
                // Gatekeeper and Zinsser use generic 'done' event
                if (event.output?.zin_doc !== undefined && event.output?.diagnosis !== undefined) {
                  // This is Zinsser output (macro editing) - has zin_doc AND diagnosis
                  callbacks.onZinsserDone?.(event.output)
                } else if (event.output?.response) {
                  // This is Gatekeeper output
                  callbacks.onGatekeeperDone?.(event.output)
                  callbacks.onDone(event.output) // Legacy compat
                }
                break
              case 'strengthen_verbs_done':
                callbacks.onStrengthenVerbsDone?.(event.output)
                break
              case 'check_logic_done':
                callbacks.onCheckLogicDone?.(event.output)
                break
              case 'thinking_start':
                callbacks.onThinkingStart?.()
                break
              case 'thinking':
                callbacks.onThinkingDelta?.(event.thinking)
                break
              case 'thinking_complete':
                callbacks.onThinkingComplete?.()
                break
              // Zinsser-specific events
              case 'zinsser_start':
                callbacks.onZinsserStart?.()
                break
              case 'zinsser_thinking_start':
                callbacks.onZinsserThinkingStart?.()
                break
              case 'zinsser_thinking':
                callbacks.onZinsserThinkingDelta?.(event.thinking)
                break
              case 'zinsser_thinking_complete':
                callbacks.onZinsserThinkingComplete?.()
                break
              // Zinsser text editor edits
              case 'zinsser_edit':
                callbacks.onZinsserEdit?.(event.old_str, event.new_str)
                break
              case 'zinsser_insert':
                callbacks.onZinsserInsert?.(event.insert_line, event.insert_text)
                break
              // StrengthenVerbs-specific events (micro editing - Pass 1)
              case 'strengthen_verbs_start':
                callbacks.onStrengthenVerbsStart?.()
                break
              case 'strengthen_verbs_thinking_start':
                callbacks.onStrengthenVerbsThinkingStart?.()
                break
              case 'strengthen_verbs_thinking':
                callbacks.onStrengthenVerbsThinkingDelta?.(event.thinking)
                break
              case 'strengthen_verbs_thinking_complete':
                callbacks.onStrengthenVerbsThinkingComplete?.()
                break
              // StrengthenVerbs text editor edits
              case 'strengthen_verbs_edit':
                callbacks.onStrengthenVerbsEdit?.(event.old_str, event.new_str)
                break
              // CheckLogic-specific events (micro editing - Pass 2)
              case 'check_logic_start':
                callbacks.onCheckLogicStart?.()
                break
              case 'check_logic_thinking_start':
                callbacks.onCheckLogicThinkingStart?.()
                break
              case 'check_logic_thinking':
                callbacks.onCheckLogicThinkingDelta?.(event.thinking)
                break
              case 'check_logic_thinking_complete':
                callbacks.onCheckLogicThinkingComplete?.()
                break
              // CheckLogic text editor edits
              case 'check_logic_edit':
                callbacks.onCheckLogicEdit?.(event.old_str, event.new_str)
                break
              // RhythmSound-specific events (micro editing - Pass 3)
              case 'rhythm_sound_start':
                callbacks.onRhythmSoundStart?.()
                break
              case 'rhythm_sound_thinking_start':
                callbacks.onRhythmSoundThinkingStart?.()
                break
              case 'rhythm_sound_thinking':
                callbacks.onRhythmSoundThinkingDelta?.(event.thinking)
                break
              case 'rhythm_sound_thinking_complete':
                callbacks.onRhythmSoundThinkingComplete?.()
                break
              // RhythmSound text editor edits
              case 'rhythm_sound_edit':
                callbacks.onRhythmSoundEdit?.(event.old_str, event.new_str)
                break
              case 'rhythm_sound_done':
                callbacks.onRhythmSoundDone?.(event.output)
                break
              // Session complete (workflow finished - includes final diff)
              case 'session_complete':
                if (event.output) {
                  callbacks.onSessionComplete?.(event.output as SessionCompleteOutput)
                }
                break
              // Responder-specific events (coaching response after editing)
              case 'responder_start':
                callbacks.onResponderStart?.()
                break
              case 'responder_thinking_start':
                callbacks.onResponderThinkingStart?.()
                break
              case 'responder_thinking':
                callbacks.onResponderThinkingDelta?.(event.thinking)
                break
              case 'responder_thinking_complete':
                callbacks.onResponderThinkingComplete?.()
                break
              case 'responder_done':
                callbacks.onResponderDone?.(event.output)
                break
            }
          }
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}


// ═══════════════════════════════════════════════════════════════════════════
// Zinsser Anatomy (Structure Parsing + Craft Insight)
// ═══════════════════════════════════════════════════════════════════════════

// Raw API response types (word-level tags from Haiku)
export interface AnatomyAPIWordTag {
  text: string
  role: string
  detail?: string
}

export interface AnatomyAPISentence {
  text: string
  words: AnatomyAPIWordTag[]
  opener_type?: string
  voice?: string
  verb_strength?: string
}

export interface AnatomyAPIParagraph {
  sentences: AnatomyAPISentence[]
}

export interface AnatomyParseResponse {
  paragraphs: AnatomyAPIParagraph[]
  content_hash: string
}

export interface AnatomyInsightResponse {
  observations: string[]
  content_hash: string
}

export async function parseAnatomyStructure(content: string): Promise<AnatomyParseResponse> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/zinsser/anatomy/parse`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  })
  if (!res.ok) throw new Error(`Anatomy parse failed: ${res.status}`)
  return res.json()
}

export async function getAnatomyInsight(
  text: string,
  scope: 'sentence' | 'paragraph',
  context?: string,
  structure?: Record<string, unknown>,
): Promise<AnatomyInsightResponse> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/zinsser/anatomy/insight`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, scope, context, structure }),
  })
  if (!res.ok) throw new Error(`Anatomy insight failed: ${res.status}`)
  return res.json()
}

// ═══════════════════════════════════════════════════════════════════════════
// Author — commissioning stream
// ═══════════════════════════════════════════════════════════════════════════

export interface AuthorCommissioningDoneEvent {
  session_id: string
  phase: string
  phase_advanced: boolean
  total_turns: number
  proposal_text: string | null
  author_id: string | null
}

export interface AuthorCommissioningStudioDoneEvent {
  response: string
  signals_update: Record<string, unknown>
  phase_full: boolean
  next_phase: string | null
  session_id: string
  thinking?: string
}

export async function streamAuthorCommission(
  params: {
    session_id: string | null
    content: string
    run_mode?: 'fast' | 'deep'
    intent?: 'accept' | 'pushback' | null
  },
  callbacks: {
    onThinkingStart?: () => void
    onThinkingDelta?: (delta: string) => void
    onThinkingComplete?: () => void
    // Streaming text from the studio (if backend emits raw text deltas)
    onTextDelta?: (delta: string) => void
    // Done event from an interviewer node — flows into the chat transcript
    onStudioDone?: (output: AuthorCommissioningStudioDoneEvent) => void
    // Done event specifically from ProposalNode — DOES NOT go into chat;
    // renders as a ProposalCard instead
    onProposalDone?: (output: AuthorCommissioningStudioDoneEvent & { proposal_text?: string }) => void
    // Done event from ContractNode — flows into chat (confirmation message)
    onContractDone?: (output: AuthorCommissioningStudioDoneEvent & { author_id?: string }) => void
    // Final commissioning_done event from SessionManager
    onCommissioningDone?: (output: AuthorCommissioningDoneEvent) => void
    onError: (error: string) => void
  },
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/events/author/commission/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      session_id: params.session_id,
      content: params.content,
      run_mode: params.run_mode ?? 'deep',
      intent: params.intent ?? null,
    }),
  })

  if (!response.ok) {
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') return

        try {
          const event = JSON.parse(data)
          if (!('type' in event)) continue

          switch (event.type) {
            case 'thinking_start':
              callbacks.onThinkingStart?.()
              break
            case 'thinking':
              callbacks.onThinkingDelta?.(event.thinking ?? '')
              break
            case 'thinking_complete':
              callbacks.onThinkingComplete?.()
              break
            case 'text':
              callbacks.onTextDelta?.(event.text ?? '')
              break
            case 'subject_exploration_done':
            case 'lens_clarification_done':
            case 'voice_triangulation_done':
            case 'source_seeding_done':
              if (event.output) {
                callbacks.onStudioDone?.(event.output as AuthorCommissioningStudioDoneEvent)
              }
              break
            case 'proposal_done':
              if (event.output) {
                callbacks.onProposalDone?.(event.output)
              }
              break
            case 'contract_done':
              if (event.output) {
                callbacks.onContractDone?.(event.output)
              }
              break
            case 'commissioning_done':
              if (event.output) {
                callbacks.onCommissioningDone?.(
                  event.output as AuthorCommissioningDoneEvent,
                )
              }
              break
            case 'commissioning_error':
              callbacks.onError(event.error ?? 'unknown error')
              break
          }
        } catch (e) {
          console.error('Failed to parse commissioning SSE event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}


// ═══════════════════════════════════════════════════════════════════════════
// Concierge — chat + sessions + SSE streaming
// ═══════════════════════════════════════════════════════════════════════════

export interface ConciergeSessionListItem {
  session_id: string
  name: string | null
  total_turns: number
  last_block_path: string | null
  pinned: boolean
  created_at: string
  updated_at: string
  last_message_at: string | null
}

export interface ConciergeTurn {
  turn_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  mode: string
  thinking: string | null
  timestamp: string
}

export interface ConciergeSessionDetail {
  session_id: string
  name: string | null
  total_turns: number
  last_block_path: string | null
  session_context: Record<string, unknown> | null
  created_at: string
  updated_at: string
  last_message_at: string | null
  turns: ConciergeTurn[]
}

/** SSE event the backend emits when the agent starts using a tool.
 *
 * Shape matches what `triage/node.py` actually yields — `name` + `input`
 * (NOT the Anthropic-API-native `tool_use_id` / `tool_name` /
 * `tool_input` naming). There's no id on the wire, so the client pairs
 * results to calls positionally (each result corresponds to the most
 * recent unfinished call). */
export interface ConciergeToolCall {
  type: 'concierge_tool_call'
  name: string
  input?: Record<string, unknown>
}

/** SSE event when a tool returns. Backend pre-formats `summary` via
 * `_summarize_result(name, input, result)` so the client can just show
 * the string verbatim — no need to reconstruct it from the raw result. */
export interface ConciergeToolResult {
  type: 'concierge_tool_result'
  name: string
  ok: boolean
  summary?: string
  /** Optional: backend tacks these on for create_block so the client can
   * auto-open the new folder. */
  block_path?: string
  folder_name?: string
}

/** Final SSE event — carries the assistant's text + session id. The
 * `response` field name comes from session_manager/node.py. */
export interface ConciergeSessionComplete {
  type: 'session_complete'
  session_id?: string
  response?: string
  saved?: boolean
}

/** Per-attachment payload — matches ConciergeAttachment in
 *  app/workflows/concierge/schema.py. The backend validates these
 *  fields server-side; the client just needs to honor the wire shape. */
export interface ConciergeAttachmentPayload {
  kind: 'image' | 'document'
  mime: string
  filename: string
  data_base64: string
}

interface StreamConciergeParams {
  content: string
  /** Pass null on first message — backend lazy-creates and returns the new id. */
  session_id: string | null
  /** Path of the active block if the user is "looking at" one. Optional. */
  block_path?: string | null
  active_file_path?: string | null
  project_path?: string | null
  universe_path?: string | null
  /** Review variant (only used when `mode === 'review'`). */
  variant?: 'daily' | 'weekly' | 'ninety_day'
  /** Multimodal attachments pasted or dropped into the input. */
  attachments?: ConciergeAttachmentPayload[]
  /** Penn Review only. ISO YYYY-MM-DD lower bound. Required when mode === 'penn_review'. */
  date_from?: string
  /** Penn Review only. ISO YYYY-MM-DD upper bound. Required when mode === 'penn_review'. */
  date_to?: string
  /** Penn Review only. Optional emphasis note for the per-workstream agents. */
  context?: string
  /** Penn Review only. If false, the consolidator's card is held in the chat
   *  pane and NOT written to penn/Penn Review/. Default true. */
  write_to_vault?: boolean
  /** Wrap-up only. When true, this turn runs the agent AUTONOMOUSLY (headless
   *  directive: review evidence → write outcome + wrap-up card + CONCIERGE.md
   *  → archive), streamed. Used for the auto-greet first turn when opened from
   *  the calendar wrap-up link. Subsequent user turns omit it and run as a
   *  normal interactive conversation on the same session. */
  headless?: boolean
}

/**
 * Endpoint suffix per chat mode. All four streaming endpoints share the
 * same SSE event vocabulary (`thinking_*`, `concierge_tool_*`,
 * `session_complete`, errors) — they only differ in the workflow that
 * runs server-side and the `mode` column on persisted turns.
 */
export type ConciergeStreamMode = 'default' | 'wrapup' | 'prep' | 'review' | 'penn_review'

const STREAM_PATH_BY_MODE: Record<ConciergeStreamMode, string> = {
  default: '/events/concierge/stream',
  wrapup: '/events/concierge/wrapup/stream',
  prep: '/events/concierge/prep/stream',
  review: '/events/concierge/review/stream',
  penn_review: '/events/concierge/penn-review/stream',
}

/** Penn Review SSE events the backend emits in addition to the
 *  shared event vocabulary. Forwarded verbatim via `onCustomEvent`
 *  so the UI can render per-workstream progress + capture the final
 *  written file path. */
export interface PennReviewEvent {
  type:
    | 'penn_orchestrator_done'
    | 'penn_workstreams_starting'
    | 'penn_workstream_done'
    | 'penn_consolidator_start'
    | 'penn_review_done'
  workstream_count?: number
  workstream_names?: string[]
  workstream?: string
  is_empty?: boolean
  ok?: boolean
  error?: string | null
  turns?: number
  progress?: { done: number; total: number }
  past_review_count?: number
  window?: { date_from: string; date_to: string; pretty: string }
  total?: number
  max_parallel?: number
  response?: string
  written_path?: string | null
  write_error?: string | null
}

interface StreamConciergeCallbacks {
  onThinkingStart?: () => void
  onThinkingDelta?: (delta: string) => void
  onThinkingComplete?: (final?: string) => void
  onToolCall?: (ev: ConciergeToolCall) => void
  onToolResult?: (ev: ConciergeToolResult) => void
  /** Optional: some backends emit `content_delta` for streamed assistant text. */
  onContentDelta?: (delta: string) => void
  onComplete: (ev: ConciergeSessionComplete) => void
  onError: (msg: string) => void
  /** Optional: backend-specific custom events (e.g. Penn Review's
   *  `penn_workstream_done`). Mirrors the Obsidian plugin's onCustomEvent
   *  forwarder so the web UI can render bespoke per-workflow signals. */
  onCustomEvent?: (ev: PennReviewEvent | { type: string; [k: string]: unknown }) => void
}

/**
 * POST /api/events/concierge/stream with SSE. Decodes `data: <json>\n\n`
 * frames and dispatches to per-event callbacks. Resolves when the stream
 * ends or errors.
 *
 * Mirrors the Obsidian plugin's SSE consumer: same event-type set
 * (thinking_*, concierge_tool_*, session_complete, session_error, error)
 * so a single backend serves both clients.
 */
export async function streamConcierge(
  params: StreamConciergeParams,
  callbacks: StreamConciergeCallbacks,
  mode: ConciergeStreamMode = 'default'
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  // Penn Review's PennReviewEventInput schema is structurally different —
  // no `content` field, requires `date_from` + `date_to`. Build a tight
  // body for that mode so we don't ship fields the schema ignores.
  // Other modes keep the existing shared body shape.
  let body: Record<string, unknown>
  if (mode === 'penn_review') {
    body = {
      session_id: params.session_id,
      date_from: params.date_from,
      date_to: params.date_to,
      context: params.context ?? '',
      write_to_vault: params.write_to_vault ?? true,
    }
  } else {
    body = {
      content: params.content,
      session_id: params.session_id, // null is fine — backend lazy-creates
    }
    if (params.block_path) body.block_path = params.block_path
    if (params.active_file_path) body.active_file_path = params.active_file_path
    if (params.project_path) body.project_path = params.project_path
    if (params.universe_path) body.universe_path = params.universe_path
    if (params.variant) body.variant = params.variant
    if (params.headless) body.headless = true
    if (params.attachments && params.attachments.length) {
      body.attachments = params.attachments
    }
  }

  const path = STREAM_PATH_BY_MODE[mode]

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(body),
    })
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'Network error')
    return
  }

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are double-newline-separated. The last fragment may be a
      // partial frame; keep it for the next iteration.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() || ''

      for (const frame of frames) {
        if (!frame.startsWith('data: ')) continue
        const raw = frame.slice(6)
        if (raw === '[DONE]') return
        let ev: { type?: string; [k: string]: unknown }
        try {
          ev = JSON.parse(raw)
        } catch {
          continue
        }
        switch (ev.type) {
          case 'thinking_start':
            callbacks.onThinkingStart?.()
            break
          case 'thinking':
            callbacks.onThinkingDelta?.(String(ev.thinking ?? ''))
            break
          case 'thinking_complete':
            callbacks.onThinkingComplete?.(
              ev.thinking !== undefined ? String(ev.thinking) : undefined
            )
            break
          case 'content_delta':
            callbacks.onContentDelta?.(String(ev.delta ?? ev.content ?? ''))
            break
          case 'concierge_tool_call':
            callbacks.onToolCall?.(ev as unknown as ConciergeToolCall)
            break
          case 'concierge_tool_result':
            callbacks.onToolResult?.(ev as unknown as ConciergeToolResult)
            break
          case 'session_complete':
            callbacks.onComplete(ev as unknown as ConciergeSessionComplete)
            break
          case 'session_error':
          case 'error':
            callbacks.onError(String(ev.error ?? 'Unknown error'))
            break
          default:
            // Backend may emit workflow-specific events (e.g. Penn Review's
            // penn_workstream_done) — forward verbatim so the UI can decide
            // what to do with them. Mirrors the Obsidian plugin's pattern.
            callbacks.onCustomEvent?.(ev as PennReviewEvent | { type: string; [k: string]: unknown })
            break
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/** GET /api/concierge/sessions — list recent sessions, newest first. */
export async function listConciergeSessions(
  limit = 50
): Promise<ConciergeSessionListItem[]> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/concierge/sessions?limit=${limit}`, {
    headers: authHeaders,
  })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`listConciergeSessions: HTTP ${res.status}`)
  }
  const data = await res.json()
  // The backend wraps in `{ sessions: [...] }` or returns the array directly.
  // Be tolerant of either shape.
  return Array.isArray(data) ? data : data.sessions || []
}

/** PATCH /api/concierge/sessions/<id> — update mutable session fields
 * (pin, rename, archive). Returns the updated summary. */
export async function updateConciergeSession(
  sessionId: string,
  body: { pinned?: boolean; name?: string; status?: string }
): Promise<ConciergeSessionListItem> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/concierge/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`updateConciergeSession: HTTP ${res.status}`)
  }
  return res.json()
}

/** GET /api/concierge/sessions/<id> — full session detail incl. turns. */
export async function getConciergeSession(
  sessionId: string,
  turnLimit = 100
): Promise<ConciergeSessionDetail> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(
    `${API_BASE}/concierge/sessions/${sessionId}?turn_limit=${turnLimit}`,
    { headers: authHeaders }
  )
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`getConciergeSession: HTTP ${res.status}`)
  }
  return res.json()
}

// ─── Wrap-up walks ─────────────────────────────────────────────────────────
//
// The walks API returns the next block in the attention queue (overdue /
// completed-unscored / missing). Two entry points: /start returns the first
// block in the queue; /next returns what comes after a just-finished block.
// Both return the same shape so the caller can use one handler for both.

export interface ConciergeWalkBlock {
  path: string // absolute folder path
  name: string // folder name (with timestamp prefix)
  title: string // human-readable title (timestamp stripped)
  universe: string
  project: string
  status: string // overdue / completed-unscored / missing
  output_intent: string
  // Calendar-style LOCATION string: `<domain> - [<parent> -] <workstream>
  // - <YYMMDD-HHMMSS>`. Same shape watchers write into iCal events, so
  // the wrap-up banner can show the user the same context they see in
  // their calendar app. Empty when the folder timestamp can't be parsed.
  location?: string
}

export type ConciergeWalkResponse =
  | { kind: 'vault_cleared' }
  | {
      kind: 'next'
      next_block: ConciergeWalkBlock
      same_project: boolean
      crossed_universe: boolean
    }
  | { kind: 'error'; error: string }

export interface ConciergeWalkPreviewBlock {
  path: string
  name: string
  title: string
  universe: string
  project: string
  status: string
  output_intent?: string
  /** Calendar-style LOCATION string — see ConciergeWalkBlock.location. */
  location?: string
}

export interface ConciergeWalkPreview {
  universes: Array<{
    name: string
    count: number
    blocks: ConciergeWalkPreviewBlock[]
  }>
  total: number
}

/** GET /api/concierge/walks/preview — attention queue grouped by universe.
 *
 * Powers the universe-picker on the web wrap-up button. Each universe
 * carries both a count and the full list of pending blocks so the picker
 * can render an expand-to-drill UI: click a universe to see its blocks,
 * click a block to open just that wrap-up (no walk).
 */
export async function getConciergeWalkPreview(): Promise<ConciergeWalkPreview> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/concierge/walks/preview`, {
    headers: authHeaders,
  })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`getConciergeWalkPreview: HTTP ${res.status}`)
  }
  return res.json()
}

/** GET /api/concierge/walks/start — first attention-queue block. */
export async function startConciergeWalk(
  universe?: string
): Promise<ConciergeWalkResponse> {
  const authHeaders = await getAuthHeaders()
  const qs = universe ? `?universe=${encodeURIComponent(universe)}` : ''
  const res = await fetch(`${API_BASE}/concierge/walks/start${qs}`, {
    headers: authHeaders,
  })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`startConciergeWalk: HTTP ${res.status}`)
  }
  return res.json()
}

/** GET /api/concierge/walks/next?current=<path>&universe=<u> — next block after `current`.
 *
 * Optional `universe` filter persists the constraint across walk advances when
 * the walk was started universe-filtered (e.g. from master.md's penn chip).
 * Omitted → walk crosses universe boundaries alphabetically (Obsidian's
 * longstanding behavior).
 */
export async function getNextConciergeWalk(
  currentPath: string,
  universe?: string
): Promise<ConciergeWalkResponse> {
  const authHeaders = await getAuthHeaders()
  const params = new URLSearchParams({ current: currentPath })
  if (universe) params.set('universe', universe)
  const res = await fetch(
    `${API_BASE}/concierge/walks/next?${params.toString()}`,
    { headers: authHeaders }
  )
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`getNextConciergeWalk: HTTP ${res.status}`)
  }
  return res.json()
}

// ─── Cockpit — server-rendered dashboard + quick capture ───────────────────
//
// The mobile-first web Cockpit (route /cockpit) reads the same payload the
// Obsidian plugin's CockpitView renders: GET /concierge/cockpit, computed on
// the server where the watcher writes frontmatter. Quick capture posts to
// /concierge/capture/* — direct card/block creation, no agent in the loop.
// Vocabulary: domain / workstream / block (Walker Architecture).

export interface CockpitHeroBlockRef {
  name: string
  path: string
  domain: string
  workstream: string | null
  start: string | null
  end: string | null
  all_day: boolean
  status: string | null
}

export interface CockpitHero {
  now: CockpitHeroBlockRef | null
  next: CockpitHeroBlockRef | null
  today: { minutes: number; blocks: number }
  week: { minutes: number; blocks: number }
  last24h: { minutes: number; delta_pct: number | null }
}

export interface CockpitDomain {
  name: string
  minutes_12mo: number
  active_workstreams: number
  total_workstreams: number
}

export interface CockpitWorkstream {
  domain: string
  name: string
  path: string
  state: 'active' | 'inactive' | 'archived'
  last_activity: string | null
  minutes_30d: number
  open_blocks: number
  /** 12 weekly buckets, oldest → newest (sparkline). */
  spark?: number[]
  /** minutes_30d vs the prior 30d window. */
  delta_pct?: number | null
}

export interface CockpitBlockRow {
  name: string
  path: string
  domain: string
  workstream: string | null
  status: string | null
  start: string | null
  end: string | null
  all_day: boolean
  wrapped: boolean
  archived: boolean
  output_intent: string | null
}

export interface CockpitCard {
  name: string
  path: string
  domain: string
  workstream: string | null
  ext: string
  mtime: string
}

export interface CockpitPlanItem {
  summary: string
  domain: string
  start: string | null
  end: string | null
  start_ms: number
  end_ms: number
}

export interface CockpitActualItem {
  name: string // display name (timestamp stripped)
  path: string
  domain: string
  workstream: string | null
  calendar_type: string | null
  status: string | null
  start: string | null
  end: string | null
  start_ms: number
  end_ms: number
}

export interface CockpitDayKpis {
  committed_min: number
  filled_min: number
  unmet_min: number
  plan_pct: number | null
  void_min?: number
}

export interface CockpitTimelineDay {
  date: string
  day_start: string
  plan: CockpitPlanItem[]
  actual: CockpitActualItem[]
  kpis: CockpitDayKpis
}

export interface CockpitAttentionGroup {
  name: string // display name (timestamp stripped)
  domain: string
  workstream: string | null
  status: string
  count: number
  items: Array<{
    name: string // full folder name (with timestamp)
    path: string
    start: string | null
    wrapped: boolean
    output_intent: string | null
  }>
  latest_start: string | null
}

export interface CockpitRecentRow {
  kind: 'block' | 'card'
  name: string
  path: string
  domain: string
  workstream: string | null
  status: string | null
  mtime: string
}

export interface CockpitPayload {
  ok: boolean
  generated_at: string
  vault_root: string
  live_overlay: boolean
  hero: CockpitHero
  timeline: { days: CockpitTimelineDay[]; today: CockpitTimelineDay | null }
  domains: CockpitDomain[]
  workstreams: CockpitWorkstream[]
  attention: CockpitBlockRow[]
  attention_groups: CockpitAttentionGroup[]
  recent: CockpitRecentRow[]
  upcoming: CockpitBlockRow[]
  cards: CockpitCard[]
  build_ms: number
  stale_seconds?: number
}

export interface CockpitSearchResult {
  ok: boolean
  error?: string
  took_ms?: number
  exact?: Array<{
    kind: 'block' | 'card'
    name: string
    path: string
    domain: string
    workstream: string | null
    snippet?: string
  }>
  semantic?: Array<{
    path: string
    block_name?: string
    universe?: string
    project?: string
    similarity?: number
    excerpt?: string
  }>
}

/** GET /api/concierge/cockpit/search — exact-syntax + semantic vault search. */
export async function searchCockpit(q: string, semantic = true): Promise<CockpitSearchResult> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(
    `${API_BASE}/concierge/cockpit/search?q=${encodeURIComponent(q)}&semantic=${semantic}`,
    { headers: authHeaders }
  )
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`searchCockpit: HTTP ${res.status}`)
  }
  return res.json()
}

export type VaultFileResult =
  | { ok: true; kind: 'text'; name: string; path: string; content: string; truncated: boolean; extractor: string }
  | { ok: true; kind: 'binary'; name: string; path: string; media_type: string }

/** GET /api/concierge/vault-file — read a vault file (or folder → folder note). */
export async function getVaultFile(path: string): Promise<VaultFileResult> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(
    `${API_BASE}/concierge/vault-file?path=${encodeURIComponent(path)}`,
    { headers: authHeaders }
  )
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    let detail = ''
    try {
      const data = await res.json()
      detail = typeof data.detail === 'string' ? data.detail : ''
    } catch {
      /* non-JSON */
    }
    throw new Error(detail || `getVaultFile: HTTP ${res.status}`)
  }
  return res.json()
}

/** Fetch a binary vault file (PDF/image) as an object URL for <img>/<iframe>.
 * Caller must URL.revokeObjectURL when done. */
export async function getVaultFileObjectUrl(path: string): Promise<string> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(
    `${API_BASE}/concierge/vault-file/raw?path=${encodeURIComponent(path)}`,
    { headers: authHeaders }
  )
  if (!res.ok) throw new Error(`getVaultFileRaw: HTTP ${res.status}`)
  return URL.createObjectURL(await res.blob())
}

/** GET /api/concierge/cockpit — full dashboard payload (server SWR cache). */
export async function getCockpit(refresh = false): Promise<CockpitPayload> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(
    `${API_BASE}/concierge/cockpit${refresh ? '?refresh=true' : ''}`,
    { headers: authHeaders }
  )
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`getCockpit: HTTP ${res.status}`)
  }
  return res.json()
}

export interface CaptureCardParams {
  workstream_path: string
  content: string
  title?: string
}

export interface CaptureCardResult {
  ok: boolean
  path: string
  filename: string
  title?: string
}

export interface CaptureBlockParams {
  workstream_path: string
  title: string
  type: 'Governance' | 'Operations' | 'Development' | 'Distribution'
  date: string // YYYY-MM-DD
  time?: string // HH:MM — omit for all-day
  duration_minutes?: number
  fast?: boolean
  output_intent?: string // blank → server writes a marked placeholder
}

export interface CaptureBlockResult {
  ok: boolean
  path: string
  folder_name: string
  calendar_type?: string
  calendar_status?: string
  timed?: boolean
}

async function postCaptureJson<T>(path: string, body: unknown, label: string): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href =
        '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    // 422 carries the tool's human-readable error in `detail` — surface it.
    let detail = ''
    try {
      const data = await res.json()
      detail = typeof data.detail === 'string' ? data.detail : ''
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `${label}: HTTP ${res.status}`)
  }
  return res.json()
}

/** POST /api/concierge/capture/card — write a card into a workstream. */
export function captureCard(params: CaptureCardParams): Promise<CaptureCardResult> {
  return postCaptureJson('/concierge/capture/card', params, 'captureCard')
}

/** POST /api/concierge/capture/block — create a block (folder + note + event). */
export function captureBlock(params: CaptureBlockParams): Promise<CaptureBlockResult> {
  return postCaptureJson('/concierge/capture/block', params, 'captureBlock')
}

/** POST /api/events/concierge/wrapup/enqueue — queue a headless wrap-up. */
export function enqueueWrapup(blockPath: string): Promise<{ ok: boolean; queued: boolean }> {
  return postCaptureJson('/events/concierge/wrapup/enqueue', { block_path: blockPath, trigger: 'manual' }, 'enqueueWrapup')
}

/** POST /api/events/concierge/archive-block — direct archive of a block. */
export function archiveBlockDirect(blockPath: string): Promise<{ ok: boolean }> {
  return postCaptureJson('/events/concierge/archive-block', { block_path: blockPath }, 'archiveBlockDirect')
}

// ═══════════════════════════════════════════════════════════════════════════
// Vault tree (vault_nodes projection) — powers the /vault three-pane workspace
// ═══════════════════════════════════════════════════════════════════════════

export interface VaultNode {
  id: string
  node_type: 'domain' | 'workstream' | 'block' | 'card'
  parent_id: string | null
  path: string
  depth: number
  domain: string | null
  workstream: string | null
  name: string
  is_archived: boolean
  calendar_status: string | null
  calendar_date: string | null
  calendar_end: string | null
  calendar_all_day: boolean | null
  calendar_type: string | null
  block_wrapped: boolean | null
  output_intent: string | null
  ext: string | null
  mime: string | null
  size_bytes: number | null
  created_ts: string | null
  mtime: string | null
  child_count?: number
  has_children?: boolean
  // non-archived block count among a node's children (set on workstream rows)
  active_block_count?: number
  // domain-root rollups (only on /tree/roots)
  active_ws?: number
  archived_ws?: number
  active_blocks?: number
}

export interface VaultCrumb {
  id: string
  name: string
  node_type: string
}

export interface VaultPagePayload {
  node: VaultNode
  summary: Record<string, number>
  children: VaultNode[]
  breadcrumb: VaultCrumb[]
}

async function getVaultJson<T>(path: string, label: string): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`${label}: HTTP ${res.status}`)
  }
  return res.json()
}

/** GET /api/concierge/tree/roots — domains with rollup counts. */
export function getTreeRoots(): Promise<VaultNode[]> {
  return getVaultJson('/concierge/tree/roots', 'getTreeRoots')
}

/** GET /api/concierge/tree?parent_id= — immediate children of a node. */
export function getTreeChildren(parentId: string): Promise<VaultNode[]> {
  return getVaultJson(`/concierge/tree?parent_id=${encodeURIComponent(parentId)}`, 'getTreeChildren')
}

/** GET /api/concierge/page/{id} — node + status summary + children. */
export function getNodePage(nodeId: string): Promise<VaultPagePayload> {
  return getVaultJson(`/concierge/page/${encodeURIComponent(nodeId)}`, 'getNodePage')
}

/** GET /api/concierge/resolve?path= — resolve a vault-link href (vault-relative
 *  path the agent emits in chat) to a node. Folder-note links resolve to their
 *  folder node. Returns null on 404 (path not in the projection). */
export async function resolveVaultNode(path: string): Promise<VaultNode | null> {
  try {
    return await getVaultJson<VaultNode>(`/concierge/resolve?path=${encodeURIComponent(path)}`, 'resolveVaultNode')
  } catch {
    return null
  }
}

/** GET /api/concierge/workstream-cards/{id} — ALL cards under a workstream,
 * recursively (loose + inside blocks + archived). */
export function getWorkstreamCards(nodeId: string): Promise<{ cards: VaultNode[] }> {
  return getVaultJson(`/concierge/workstream-cards/${encodeURIComponent(nodeId)}`, 'getWorkstreamCards')
}

export interface SearchOpts {
  q?: string
  domain?: string
  workstream?: string
  ext?: string
  semantic?: boolean
}

/** GET /api/concierge/search — name or semantic search, optionally scoped by
 * domain/workstream/ext. */
export function searchNodes(opts: SearchOpts): Promise<{ results: VaultNode[]; semantic?: boolean }> {
  const p = new URLSearchParams()
  if (opts.q) p.set('q', opts.q)
  if (opts.domain) p.set('domain', opts.domain)
  if (opts.workstream) p.set('workstream', opts.workstream)
  if (opts.ext) p.set('ext', opts.ext)
  if (opts.semantic) p.set('semantic', 'true')
  return getVaultJson(`/concierge/search?${p.toString()}`, 'searchNodes')
}

export interface VaultContent {
  ok: boolean
  name: string
  path: string
  frontmatter: string | null
  managed: string | null
  body: string
  has_marker: boolean
  sections: Record<string, string> | null
}

/** GET /api/concierge/vault-content?path= — the editable user body + the
 * read-only managed region (frontmatter + concierge fences). */
export function getCardContent(path: string): Promise<VaultContent> {
  return getVaultJson(`/concierge/vault-content?path=${encodeURIComponent(path)}`, 'getCardContent')
}

/** PUT /api/concierge/vault-content — write ONLY the user body (server splices,
 * frontmatter + managed region preserved byte-for-byte). */
export async function saveCardContent(
  path: string,
  body: string
): Promise<{ ok: boolean; path: string; bytes: number }> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/concierge/vault-content`, {
    method: 'PUT',
    headers: { ...authHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, body }),
  })
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
    }
    throw new Error(`saveCardContent: HTTP ${res.status}`)
  }
  return res.json()
}

